import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { AccountingSyncStatusFilter } from 'anchor-scout-common/types';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { UserInfoModel, MemberModel, invoiceModel, invoiceSummaryModel, BillingControlDataModel, ActivityLogEntryModel, AccountantModel, HistoricAgreementStatsModel, PayoutsSummaryModel, ReferralModel, integrationModel, invoiceDraftModel, proposalTemplateModel, relationshipModel, relationshipSummaryModel, serviceModel, serviceTemplateModel, timeModel, LatestOFACSanctionStatusModel, ProposalTemplateSummaryModel, SalesSummaryModel, TimeActivitiesModel, AgreementImportExtractionModel, ConversationModel } from '~/models';
import { AccountingCustomerModel } from '~/models/AccountingCustomerModel';
import { BusinessSettingsModel } from '~/models/BusinessSettingsModel';
import { ClientModel } from '~/models/ClientModel';
import { ContactModel } from '~/models/ContactModel';
import { DraftModel } from '~/models/DraftModel';
import { EmailOnBehalfSettingsModel } from '~/models/EmailOnBehalfSettingsModel';
import { FutureInvoiceModel } from '~/models/FutureInvoiceModel';
import { HistoricRevenueModel } from '~/models/HistoricRevenueModel';
import { InvoiceDraftDatesModel } from '~/models/InvoiceDraftDatesModel';
import { InvoicesDigestModel } from '~/models/invoices-digest/InvoicesDigestModel';
import { PayoutModel } from '~/models/PayoutModel';
import { RecipientModel } from '~/models/RecipientModel';
import { RemainingCreditReportEntryModel } from '~/models/RemainingCreditReportEntryModel';
import { ServiceStatusType } from '~/models/service/model';
import { ServiceInvoiceModel } from '~/models/ServiceInvoiceModel';
import { TeamInviteModel } from '~/models/TeamInvitesModel';
import { fromDateStampObject } from '~/models/time';
import { EXTERNAL_PAYOUT_INVOICE_HEADERS } from '~/pages/PayoutsDashboard/PayoutsDashboard.utils';
import { getProposalsReportFilterParams } from '~/pages/Reports/ProposalsReport/ProposalsReport.utils';
import { externalSortField, externalSyncFilter } from '~/store/invoice-query-helpers';
import { AccountingItemType } from '~/types/api/accounting-integration';
import { IntegrationResponseStatus } from '~/types/api.model';
import { utils } from '~/utils';
import { buildAgreementsCsvExportParams } from '~/utils/agreements-csv-export-params-builder';
import { serializeDateUtc } from '~/utils/date.utils';
import { createAgentStreamParser } from '~/utils/parseAgentStreamEvent';
import { createAnalystStreamParser } from '~/utils/parseAnalystStreamEvent';
import { buildRelationshipsParamsFilter } from '~/utils/relationships-params-filter-builder';
import { APIHost } from './sendRequest';
import { selectors } from './state-selectors';
export function initStoreApi(param) {
    let { getState, getNow, sendRequest, sendStream } = param;
    const mapResponseBody = (res)=>res.data;
    return {
        charge: {
            approveAll (relationshipId) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/billing/".concat(relationshipId, "/charge/approve"),
                    urlForSentry: '/billing/:id/charge/approve',
                    data: {}
                }).then(mapResponseBody);
            },
            async approve (relationshipId, chargeId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/billing/".concat(relationshipId, "/charge/").concat(chargeId, "/approve"),
                    urlForSentry: '/billing/:id/charge/:id/approve'
                });
            },
            async delete (relationshipId, chargeId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/billing/".concat(relationshipId, "/charge/").concat(chargeId, "/cancel"),
                    urlForSentry: '/billing/:id/charge/:id/cancel',
                    disableSentry: (err)=>err.hasServerError(ServerError.AdHocChargeNotFound)
                });
            },
            async deleteCredit (relationshipId, chargeId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/billing/".concat(relationshipId, "/credit/").concat(chargeId, "/cancel"),
                    urlForSentry: '/billing/:id/credit/:id/cancel'
                });
            },
            async pauseCredit (relationshipId, chargeId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/billing/".concat(relationshipId, "/credit/").concat(chargeId, "/pause"),
                    urlForSentry: '/billing/:id/credit/:id/pause'
                });
            },
            async resumeCredit (relationshipId, chargeId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/billing/".concat(relationshipId, "/credit/").concat(chargeId, "/resume"),
                    urlForSentry: '/billing/:id/credit/:id/resume'
                });
            }
        },
        service: {
            async togglePauseResumeBilling (relationshipId, serviceId, pause) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/service/").concat(serviceId, "/pause"),
                    urlForSentry: '/relationship/:id/service/:id/pause',
                    data: {
                        pause
                    }
                });
            },
            nextBillingDate (relationshipId, serviceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/service/").concat(serviceId, "/next-billing-date"),
                    urlForSentry: '/relationship/:id/service/:id/next-billing-date'
                }).then((res)=>timeModel.fromDateStampObject(res.data.date));
            },
            async getPromptSuggestions () {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: '/api/service-description/conversations/suggestions',
                    urlForSentry: '/api/service-description/conversations/suggestions'
                }).then(mapResponseBody);
            },
            async generateDescriptionFromTitle (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/service-description/conversations/title',
                    urlForSentry: '/api/service-description/conversations/title',
                    data
                }).then(mapResponseBody);
            },
            async generateDescriptionFromDescription (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/service-description/conversations/description',
                    urlForSentry: '/api/service-description/conversations/description',
                    data
                }).then(mapResponseBody);
            },
            async generateDescriptionFromMessage (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(data.conversationId, "/messages"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/messages",
                    data: {
                        type: data.type,
                        message: data.message
                    }
                }).then(mapResponseBody);
            },
            async generateDescriptionFromPreset (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(data.conversationId, "/messages"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/messages",
                    data: {
                        type: data.type,
                        preset: data.preset
                    }
                }).then(mapResponseBody);
            },
            async undoDescription (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(conversationId, "/undo"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/undo"
                }).then(mapResponseBody);
            },
            async acceptServiceDescription (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(conversationId, "/accept"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/accept",
                    data: {}
                }).then(mapResponseBody);
            },
            async rejectServiceDescription (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(conversationId, "/reject"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/reject",
                    data: {}
                }).then(mapResponseBody);
            },
            async submitServiceDescriptionFeedback (conversationId, data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(conversationId, "/feedback"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/feedback",
                    data
                }).then(mapResponseBody);
            }
        },
        notificationRewrite: {
            async startConversation (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/notification-rewrite/conversations',
                    data
                }).then(mapResponseBody);
            },
            async continueConversation (conversationId, data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/notification-rewrite/conversations/".concat(conversationId, "/messages"),
                    urlForSentry: '/api/notification-rewrite/conversations/:conversationId/messages',
                    data
                }).then(mapResponseBody);
            },
            async undoRewrite (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/notification-rewrite/conversations/".concat(conversationId, "/undo"),
                    urlForSentry: '/api/notification-rewrite/conversations/:conversationId/undo',
                    data: {}
                }).then(mapResponseBody);
            },
            async acceptRewrite (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/notification-rewrite/conversations/".concat(conversationId, "/accept"),
                    urlForSentry: '/api/notification-rewrite/conversations/:conversationId/accept',
                    data: {}
                }).then(mapResponseBody);
            },
            async rejectRewrite (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/notification-rewrite/conversations/".concat(conversationId, "/reject"),
                    urlForSentry: '/api/notification-rewrite/conversations/:conversationId/reject',
                    data: {}
                }).then(mapResponseBody);
            }
        },
        billing: {
            submitCharges (relationshipId, data) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.External,
                    url: "/billing/".concat(relationshipId, "/submit"),
                    urlForSentry: '/billing/:id/submit',
                    data
                }).then(mapResponseBody);
            },
            getAdHocScheduledData (relationshipId, date) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/billing/".concat(relationshipId, "/adhoc-scheduled-data"),
                    urlForSentry: '/billing/:id/adhoc-scheduled-data',
                    params: {
                        date
                    }
                }).then((res)=>res.data);
            },
            addCredit (relationshipId, data) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.External,
                    url: "/billing/".concat(relationshipId, "/credit"),
                    urlForSentry: '/billing/:id/credit',
                    data
                }).then(mapResponseBody);
            },
            scheduledBillingAttachments: {
                add (relationshipId, issueDate, attachments) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/billing/".concat(relationshipId, "/attach"),
                        urlForSentry: '/billing/:id/attach',
                        data: {
                            issueDate: {
                                year: issueDate.getFullYear(),
                                month: issueDate.getMonth() + 1,
                                day: issueDate.getDate()
                            },
                            attachments
                        }
                    }).then(mapResponseBody);
                },
                remove (relationshipId, issueDate, attachmentId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'delete',
                        url: "/billing/".concat(relationshipId, "/attachments/").concat(attachmentId),
                        urlForSentry: '/billing/:id/attachments/:attachmentId',
                        params: {
                            issueDate: timeModel.toDatestampArray(issueDate).join(',')
                        }
                    }).then(mapResponseBody);
                },
                async getDownloadUrl (relationshipId, attachmentId) {
                    const res = await sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'get',
                        url: "/billing/".concat(relationshipId, "/attachments/").concat(attachmentId, "/download"),
                        urlForSentry: '/billing/:id/attachments/:attachmentId/download'
                    });
                    return res.data.url;
                }
            },
            bulk: {
                upload (fileContent) {
                    return sendRequest({
                        method: 'post',
                        apiHost: APIHost.Mgmt,
                        url: "/billing/bulk/upload",
                        data: fileContent,
                        config: {
                            headers: {
                                'Content-Type': 'text/csv'
                            }
                        }
                    }).then(mapResponseBody);
                },
                uploadResult (bulkUploadId) {
                    return sendRequest({
                        method: 'get',
                        apiHost: APIHost.Mgmt,
                        url: "/billing/bulk/upload/".concat(bulkUploadId, "/results"),
                        urlForSentry: 'bulk/upload/:bulkUploadId/results'
                    }).then(mapResponseBody);
                },
                billClients (bulkUploadId) {
                    return sendRequest({
                        method: 'post',
                        apiHost: APIHost.Mgmt,
                        url: "/billing/bulk/upload/".concat(bulkUploadId, "/submit"),
                        urlForSentry: '/billing/bulk/upload/:bulkUploadId/submit',
                        data: bulkUploadId,
                        config: {
                            headers: {
                                'Content-Type': 'text/csv'
                            }
                        },
                        disableSentry: (err)=>err.hasServerError(ServerError.BulkBillAlreadyInProgress)
                    }).then(mapResponseBody);
                },
                getBillClientsResult (bulkUploadId) {
                    return sendRequest({
                        method: 'get',
                        apiHost: APIHost.Mgmt,
                        url: "/billing/bulk/billing/".concat(bulkUploadId, "/results"),
                        urlForSentry: '/billing/bulk/billing/:bulkUploadId/results'
                    }).then(mapResponseBody);
                }
            },
            getBillingSchedule (data, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/billing/billing-schedule",
                    urlForSentry: '/billing/billing-schedule',
                    data,
                    params
                }).then(mapResponseBody);
            }
        },
        billCommand: {
            async remove (relationshipId, serviceId, billCommandId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/service/").concat(serviceId, "/bill-command/").concat(billCommandId, "/cancel"),
                    urlForSentry: '/relationship/:id/service/:id/bill-command/:id/cancel'
                });
            }
        },
        texts: {
            getLegalTermsRichTextContent (sourceId, textId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/texts/".concat(sourceId, "/").concat(textId),
                    urlForSentry: '/texts/:sourceId/:id'
                }).then(mapResponseBody);
            }
        },
        relationship: {
            serviceBillingDetails (service, effectiveDate) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/service-billing-details",
                    urlForSentry: '/relationship/service-billing-details',
                    data: {
                        service,
                        effectiveDate
                    }
                }).then((res)=>{
                    return {
                        periods: res.data.periods,
                        nextBillingDate: res.data.nextBillingDate ? fromDateStampObject(res.data.nextBillingDate) : null
                    };
                });
            },
            getBillingSchedule (relationshipId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/billing-schedule"),
                    urlForSentry: '/relationship/:id/billing-schedule',
                    params
                }).then(mapResponseBody);
            },
            getServiceNames (businessId, isVendor, type) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/relationships-summary-mv/service-names"),
                    urlForSentry: '/business/:id/relationships-summary-mv/service-names',
                    params: {
                        party: isVendor ? 'vendor' : 'customer',
                        status: type === 'agreement' ? "".concat(ServiceStatusType.Approved, ",").concat(ServiceStatusType.Terminated) : ServiceStatusType.AwaitingApproval
                    }
                }).then((res)=>res.data.map((obj)=>obj.serviceName));
            },
            getSummaries (config) {
                const { businessId } = config;
                const filterParams = buildRelationshipsParamsFilter(config);
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/relationships-summary-mv"),
                    urlForSentry: '/business/:id/relationships-summary-mv',
                    params: filterParams
                }).then((res)=>(res.data || []).map(relationshipSummaryModel.fromDTO));
            },
            getProposalsReportSummaries (param) {
                let { isVendor, businessId, filters } = param;
                const filterParams = getProposalsReportFilterParams(filters, isVendor);
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/relationships-summary-mv"),
                    urlForSentry: '/business/:id/relationships-summary-mv',
                    params: _object_spread({}, filterParams)
                }).then((res)=>(res.data || []).map(relationshipSummaryModel.fromDTO));
            },
            /** Returns null if didn't connect any payment-method. */ getSelectedPaymentMethod (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/funding-source"),
                    urlForSentry: '/relationship/:id/funding-source'
                }).then((res)=>res.data.details);
            },
            async changePaymentMethod (relationshipId, fundingSourceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/funding-source/").concat(fundingSourceId),
                    urlForSentry: '/relationship/:id/funding-source/:id'
                });
            },
            async disconnectPaymentMethod (relationshipId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/relationship/".concat(relationshipId, "/disconnect-payment-method"),
                    urlForSentry: '/relationship/:id/disconnect-payment-method'
                });
            },
            createAdHoc (clientContactId) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/relationship/create-adhoc',
                    data: {
                        clientContactId
                    }
                }).then(mapResponseBody);
            },
            createFromDraft (draftId) {
                let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: opts.externallySign ? '/relationship/approve/draft/vendor' : '/relationship/create-from-draft',
                    data: {
                        draftId,
                        notifyPolicy: opts.sendSilently ? 'silent' : 'notify'
                    },
                    disableSentry: (e)=>e.createRelationshipDisableSentry()
                }).then(mapResponseBody);
            },
            get (relationshipId) {
                let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                const { resolvePaymentMethod, disable403Handling } = options;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId),
                    urlForSentry: '/relationship/:id',
                    params: {
                        resolvePaymentMethod
                    },
                    disable403Handling
                }).then((res)=>relationshipModel.fromDTO(res.data));
            },
            getOriginalProposal (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(id, "/latest-proposal"),
                    urlForSentry: '/relationship/:id/latest-proposal'
                }).then(mapResponseBody).then(relationshipModel.fromDTO);
            },
            getProposalFromDraft (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(id, "/proposal-from-draft"),
                    urlForSentry: '/relationship/:id/proposal-from-draft'
                }).then(mapResponseBody).then(relationshipModel.fromDTO);
            },
            /** Returns the new relationship version. */ async update (relationshipId, body) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/update"),
                    urlForSentry: "/relationship/:id/update",
                    data: body,
                    disableSentry: (err)=>err.hasServerError(ServerError.MismatchingRelationshipVersion)
                });
                return res.data;
            },
            async approve (relationshipData, additionalData) {
                const { id, version } = relationshipData;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(id, "/approve/buyer"),
                    urlForSentry: '/relationship/:id/approve/buyer',
                    data: _object_spread({
                        version
                    }, additionalData),
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.UserAlreadySigned
                        ])
                });
            },
            async signatoryApprove (relationshipData, additionalData) {
                const { id, version } = relationshipData;
                const { fundingSourceId, requestAccountantAccessApproved, clientContext } = additionalData;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(id, "/approve/signatory"),
                    urlForSentry: '/relationship/:id/approve/signatory',
                    data: {
                        version,
                        fundingSourceId,
                        requestAccountantAccessApproved,
                        clientContext
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.UserAlreadySigned
                        ])
                });
            },
            async signatoryApproveV2 (relationshipData, additionalData) {
                const { id, version } = relationshipData;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(id, "/approve/signatory/v2"),
                    urlForSentry: '/relationship/:id/approve/signatory/v2',
                    data: _object_spread({
                        version
                    }, additionalData),
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.UserAlreadySigned,
                            ServerError.FundingSourceNotFound
                        ])
                });
            },
            async approveV2 (relationshipData, additionalData) {
                const { id, version } = relationshipData;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(id, "/approve/buyer/v2"),
                    urlForSentry: '/relationship/:id/approve/buyer/v2',
                    data: _object_spread({
                        version
                    }, additionalData),
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.UserAlreadySigned,
                            ServerError.FundingSourceNotFound
                        ])
                });
            },
            async terminateAgreement (relationshipId, notification) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/terminate"),
                    urlForSentry: '/relationship/:id/terminate',
                    data: notification ? {
                        action: 'notify',
                        notification
                    } : {
                        action: 'silent'
                    },
                    disableSentry: (e)=>e.hasServerError(ServerError.RelationshipAlreadyTerminated)
                });
            },
            async requestPaymentMethod (relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/request-payment-method"),
                    urlForSentry: '/relationship/:id/request-payment-method'
                });
            },
            getNextReminder (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/next-reminder"),
                    urlForSentry: '/relationship/:id/next-reminder'
                }).then(mapResponseBody);
            },
            async associateCustomer (relationshipId, customerId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/associate-customer"),
                    urlForSentry: '/relationship/:id/associate-customer',
                    data: {
                        customerId
                    }
                });
            },
            async stopAutoApproveAmendments (relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/stop-auto-approve-amendments"),
                    urlForSentry: '/relationship/:id/stop-auto-approve-amendments'
                });
            },
            getSingleUseApprovalLink (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    disableAuth: true,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/single-use-approval-link"),
                    urlForSentry: '/relationship/:id/single-use-approval-link',
                    disableSentry: (err)=>err.hasServerError(ServerError.RelationshipAlreadyApproved)
                }).then(mapResponseBody);
            },
            getProposalLinks (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/proposal-links"),
                    urlForSentry: '/relationship/:id/proposal-links',
                    disableSentry: (err)=>err.hasServerError(ServerError.RelationshipAlreadyApproved)
                }).then((res)=>{
                    return {
                        primary: RecipientModel.fromDTO(res.data.primary),
                        additionalRecipients: res.data.additionalRecipients.map(RecipientModel.fromDTO)
                    };
                });
            },
            getBillingControlData (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/billing/".concat(relationshipId, "/control-data"),
                    urlForSentry: '/billing/:id/control-data'
                }).then((res)=>BillingControlDataModel.fromDTO(res.data));
            },
            async sendProposalApprovalReminder (relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/reminder/proposal-approval"),
                    urlForSentry: '/relationship/:id/reminder/proposal-approval'
                });
            },
            async cancelProposal (relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/cancel"),
                    urlForSentry: '/relationship/:id/cancel',
                    disableSentry: (err)=>err.hasServerError(ServerError.InvalidRelationshipStatus)
                });
            },
            withdrawForEditing (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/withdraw-for-editing"),
                    urlForSentry: '/relationship/:id/withdraw-for-editing'
                });
            },
            republish (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/republish"),
                    urlForSentry: '/relationship/:id/republish'
                });
            },
            cancelEdit (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/cancel-edit"),
                    urlForSentry: '/relationship/:id/cancel-edit'
                });
            },
            approveOnBehalfOfClient (proposalId) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/proposals/".concat(proposalId, "/approve/vendor"),
                    urlForSentry: '/proposals/:id/approve/vendor'
                }).then(mapResponseBody);
            },
            /** Will get a direct link to a relationship or an invoice, depending on the payload provided. */ getDirectLink (relationshipId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/direct-link"),
                    urlForSentry: '/relationship/:id/direct-link',
                    data: {
                        data: {
                            [data.type]: data.id
                        }
                    }
                }).then(mapResponseBody);
            },
            async approveAllInvoices (relationshipId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/relationship/".concat(relationshipId, "/approve-invoices"),
                    urlForSentry: '/relationship/:id/approve-invoices',
                    data: {}
                });
            },
            /** Updates the progressStatus of an agreement. */ async updateProgressStatus (agreementId, progressStatus) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/relationship/".concat(agreementId, "/approval-progress"),
                    urlForSentry: '/relationship/:id/approval-progress',
                    data: {
                        progressStatus
                    }
                });
            },
            approveAllAmendments (relationshipId, relationshipVersion, amendmentVersion) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/relationship/".concat(relationshipId, "/approve-amendments"),
                    urlForSentry: '/relationship/:id/approve-amendments',
                    data: {
                        version: relationshipVersion,
                        amendmentVersion
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.RelationshipAlreadyTerminated
                        ])
                }).then(mapResponseBody);
            },
            approveAmendmentFromEmail (param) {
                let { approvalToken } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/approve-amendments-by-token",
                    data: {
                        approvalToken
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.RelationshipAlreadyTerminated,
                            ServerError.NoAmendmentsToApprove
                        ])
                }).then(mapResponseBody);
            },
            async exportAllProposalsCsv () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/proposals/export-csv'
                });
                return res.data;
            },
            async exportProposalsCsvByIds (ids) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/proposals/export-csv',
                    data: {
                        ids
                    }
                });
                return res.data;
            },
            async exportAllAgreementsCsv (args) {
                const params = buildAgreementsCsvExportParams({
                    search: args.search,
                    filters: args.filters
                });
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/agreements/export-csv',
                    params
                });
                return res.data;
            },
            async exportAgreementsCsvByIds (ids) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/agreements/export-csv',
                    data: {
                        ids
                    }
                });
                return res.data;
            }
        },
        draft: {
            getFullDrafts (params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/relationship-drafts',
                    params
                }).then(mapResponseBody).then((drafts)=>drafts.map(DraftModel.fromDTO));
            },
            getSummaries () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/relationship-drafts/summary'
                }).then(mapResponseBody);
            },
            getArchivedSummaries () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/relationship-drafts/summary',
                    urlForSentry: '/relationship-drafts/summary?statuses=archived',
                    params: {
                        statuses: 'archived'
                    }
                }).then(mapResponseBody);
            },
            getById (draftId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship-drafts/".concat(draftId),
                    urlForSentry: '/relationship-drafts/:id'
                }).then((res)=>DraftModel.fromDTO(res.data));
            },
            getBillingSchedule (draftId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship-drafts/".concat(draftId, "/billing-schedule"),
                    urlForSentry: '/relationship-drafts/:id/billing-schedule',
                    params
                }).then(mapResponseBody);
            },
            save (data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/relationship-drafts/',
                    data
                }).then(mapResponseBody);
            },
            duplicate (draftId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship-drafts/".concat(draftId, "/duplicate"),
                    urlForSentry: "/relationship-drafts/:id/duplicate",
                    data: {}
                }).then(mapResponseBody);
            },
            async deleteById (draftId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'delete',
                    url: "/relationship-drafts/".concat(draftId),
                    urlForSentry: '/relationship-drafts/:id'
                });
            },
            createScheduledProposal (data) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/proposal-drafts/".concat(data.draftId, "/schedule"),
                    data: {
                        time: data.time
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.DraftAlreadyScheduled,
                            ServerError.MissingDraftOwnerDetails,
                            ServerError.InvalidScheduleTime
                        ])
                }).then(mapResponseBody);
            },
            cancelScheduledProposal (data) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/proposal-drafts/".concat(data.draftId, "/cancel-schedule"),
                    data: {}
                }).then(mapResponseBody);
            },
            unarchive (draftId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship-drafts/".concat(draftId, "/unarchive"),
                    urlForSentry: '/relationship-drafts/:id/unarchive',
                    data: {}
                }).then(mapResponseBody);
            },
            archive (draftId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship-drafts/".concat(draftId, "/archive"),
                    urlForSentry: '/relationship-drafts/:id/archive',
                    data: {}
                }).then(mapResponseBody);
            },
            async exportAllDraftsCsv () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/proposal-drafts/export-csv'
                });
                return res.data;
            },
            async exportDraftsCsvByIds (ids) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/proposal-drafts/export-csv',
                    data: {
                        ids
                    }
                });
                return res.data;
            }
        },
        client: {
            getAll () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/customers'
                }).then(mapResponseBody).then((body)=>body.customers.map((param)=>{
                        let { id, name, clientType, isSample, owner, contactsCount } = param;
                        return new ClientModel({
                            clientId: id,
                            name,
                            clientType,
                            isSample,
                            owner,
                            contactsCount
                        });
                    }));
            },
            getContacts (clientId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/v3/contacts?customerId=".concat(encodeURIComponent(clientId)),
                    urlForSentry: '/v3/contacts'
                }).then(mapResponseBody).then((body)=>body.contacts.map((c)=>new ContactModel(c)));
            },
            create (data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/customers',
                    data
                }).then(mapResponseBody).then((param)=>{
                    let { customerId } = param;
                    return {
                        clientId: customerId
                    };
                });
            },
            update (clientId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/customers/".concat(clientId),
                    urlForSentry: '/customers/:clientId',
                    data,
                    disableSentry: (e)=>e.hasServerError([
                            ServerError.DuplicateContact
                        ])
                }).then(mapResponseBody).then((param)=>{
                    let { customerId } = param;
                    return {
                        clientId: customerId
                    };
                });
            },
            createContact (clientId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v3/contacts',
                    data: _object_spread_props(_object_spread({}, data), {
                        customerId: clientId
                    }),
                    disableSentry: (e)=>e.hasPhoneValidationError() || e.hasServerError([
                            ServerError.DuplicateContact,
                            ServerError.UserWithEmailAlreadyExists
                        ])
                }).then(mapResponseBody);
            },
            updateContact (clientId, contactId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/v3/contacts/".concat(contactId),
                    urlForSentry: '/v3/contacts/:contactId',
                    data: _object_spread_props(_object_spread({}, data), {
                        customerId: clientId
                    }),
                    disableSentry: (e)=>e.hasPhoneValidationError() || e.hasServerError([
                            ServerError.DuplicateContact,
                            ServerError.ContactVersionMismatch,
                            ServerError.UserWithEmailAlreadyExists
                        ])
                }).then(mapResponseBody);
            }
        },
        contact: {
            async exportAllContactsCsv () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/contacts/export-csv'
                });
                return res.data;
            },
            async exportContactsCsvByIds (ids) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/contacts/export-csv',
                    data: {
                        ids
                    }
                });
                return res.data;
            },
            getAll () {
                return sendRequest({
                    method: 'get',
                    apiHost: APIHost.Mgmt,
                    url: '/clients'
                }).then(mapResponseBody).then((contacts)=>contacts.map((c)=>new ContactModel(c)));
            },
            getById (contactId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/contacts/".concat(contactId),
                    urlForSentry: '/contacts/:id'
                }).then((res)=>new ContactModel(res.data));
            },
            create (newContact) {
                const contactData = _object_spread_props(_object_spread({}, newContact.toJSON()), {
                    isSample: false
                });
                const disableSentry = (e)=>e.hasPhoneValidationError() || e.hasServerError([
                        ServerError.DuplicateContact,
                        ServerError.UserWithEmailAlreadyExists
                    ]);
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/contacts',
                    data: contactData,
                    disableSentry
                }).then(mapResponseBody);
            },
            update (contactId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/contacts/".concat(contactId),
                    urlForSentry: '/contacts/:id/update',
                    data,
                    disableSentry: (e)=>e.hasPhoneValidationError() || e.hasServerError([
                            ServerError.DuplicateContact,
                            ServerError.ContactVersionMismatch,
                            ServerError.ContactNotEditable,
                            ServerError.UserWithEmailAlreadyExists
                        ])
                }).then(mapResponseBody);
            },
            async archive (contactId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/clients/".concat(contactId, "/archive"),
                    urlForSentry: '/clients/:id/archive',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.FailedToArchiveContactDueToExistingRelationships,
                            ServerError.ContactVersionMismatch
                        ])
                });
            },
            async restore (contactId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/clients/".concat(contactId, "/restore"),
                    urlForSentry: '/clients/:id/restore',
                    data,
                    disableSentry: (err)=>err.hasServerError(ServerError.ContactVersionMismatch)
                });
            },
            qboCreate (contactId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/clients/".concat(contactId, "/qbo-create"),
                    urlForSentry: '/clients/:id/qbo-create',
                    disableSentry: (err)=>err.hasServerError(ServerError.DuplicateNameExists)
                }).then(mapResponseBody);
            },
            async delete (contactId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/clients/".concat(contactId, "/delete"),
                    urlForSentry: '/clients/:id/delete',
                    disableSentry: (err)=>err.hasServerError(ServerError.FailedToDeleteContactDueToExistingRelationships)
                });
            },
            async bulkArchive (request) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/contacts/bulk-archive',
                    urlForSentry: '/contacts/bulk-archive',
                    data: request
                });
                return res.data;
            },
            async bulkDelete (request) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/contacts/bulk-delete',
                    urlForSentry: '/contacts/bulk-delete',
                    data: request
                });
                return res.data;
            }
        },
        auth: {
            loginEmailStep (email, pageUrl) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/login/flow/".concat(email),
                    urlForSentry: '/login/flow/:email',
                    disableAuth: true,
                    config: {
                        headers: {
                            'Anchor-Referrer': pageUrl
                        }
                    }
                }).then(mapResponseBody);
            },
            loginWithPassword (data) {
                const { email, password, utms, anchorReferrer } = data;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/login',
                    data: {
                        email,
                        password,
                        utms
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400
                        ]),
                    config: {
                        headers: {
                            'Anchor-Referrer': anchorReferrer
                        }
                    }
                }).then(mapResponseBody);
            },
            async xeroSignUp (data) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/integrations/accounting/xero/sign-up',
                    data
                });
                return res.data;
            },
            async xeroSignIn (data) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/integrations/accounting/xero/sign-in',
                    data
                });
                return res.data;
            },
            async qboSignUp (data) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/integrations/accounting/qbo/sign-up',
                    data
                });
                return res.data;
            },
            async qboSignIn (data) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/integrations/accounting/qbo/sign-in',
                    data
                });
                return res.data;
            },
            async signupOld (param) {
                let { fullName, email, password, utms, pageUrl } = param;
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/signup-old',
                    data: {
                        fullName,
                        email,
                        password,
                        utms
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400,
                            409
                        ]),
                    config: {
                        headers: {
                            'Anchor-Referrer': pageUrl
                        }
                    }
                });
                return res.data;
            },
            async signup (param) {
                let { fullName, email, password, utms, pageUrl, clientContext } = param;
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/signup',
                    data: {
                        fullName,
                        email,
                        password,
                        utms,
                        clientContext
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400,
                            409
                        ]),
                    config: {
                        headers: {
                            'Anchor-Referrer': pageUrl
                        }
                    }
                });
                return res.data;
            },
            async approveTos (userId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/user/".concat(userId, "/approve-tos"),
                    urlForSentry: '/user/:id/approve-tos'
                });
            },
            async setPassword (password) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/password/set',
                    data: {
                        password
                    },
                    forceAuthorizationHeader: true
                });
                return res.data;
            },
            getUserInfo () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/user/me?expand=worksAt",
                    disableSentry: (err)=>err.hasStatus([
                            401
                        ])
                }).then((res)=>res.data ? UserInfoModel.fromDTO(res.data) : null).catch((err)=>{
                    var _err_response;
                    if (((_err_response = err.response) === null || _err_response === void 0 ? void 0 : _err_response.status) === 401) {
                        // This will happen when the user is removed from his team by another team-member.
                        // Should trigger an automatic logout.
                        return null;
                    }
                    throw err;
                });
            },
            getUserInfoV2 () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/user/v2/me?expand=worksAt",
                    disableSentry: (err)=>err.hasStatus([
                            401
                        ])
                }).then((res)=>res.data ? UserInfoModel.fromDTO(res.data) : null).catch((err)=>{
                    var _err_response;
                    if (((_err_response = err.response) === null || _err_response === void 0 ? void 0 : _err_response.status) === 401) {
                        return null;
                    }
                    throw err;
                });
            },
            getAccountantAccessBusinesses () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/user/me/accountant-access-businesses'
                }).then((res)=>res.data.businessIds);
            },
            getUserVideos () {
                return sendRequest({
                    method: 'get',
                    url: "/users/videos",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            },
            editUserDetails (userDetails) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me',
                    data: userDetails,
                    disableSentry: (err)=>err.hasStatus([
                            400
                        ])
                }).then(mapResponseBody);
            },
            googleSignIn (param) {
                let { pageUrl, idToken, utms, ensureEmail, clientContext } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/oauth',
                    data: {
                        provider: 'google',
                        id_token: idToken,
                        utms,
                        ensureEmail,
                        clientContext
                    },
                    config: {
                        headers: {
                            'Anchor-Referrer': pageUrl
                        }
                    },
                    disable403Handling: true,
                    disableSentry: (err)=>err.hasStatus([
                            403
                        ]) || err.hasServerError([
                            ServerError.EmailIsInvalid
                        ])
                }).then((res)=>res.data.authToken);
            },
            async resetPassword (email) {
                await sendRequest({
                    method: 'post',
                    url: '/api/forgot-password/reset',
                    data: {
                        email
                    },
                    disableAuth: false,
                    forceAuthorizationHeader: true,
                    apiHost: APIHost.Old
                });
            },
            async resendEmailVerification (email) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/resend-email-verification',
                    data: {
                        recipient: email
                    },
                    disableAuth: true
                });
            },
            requestNewOTP (email) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/login/otp/send',
                    data: {
                        email
                    },
                    disableSentry: (err)=>err.hasStatus([
                            429
                        ])
                }).then(mapResponseBody);
            },
            requestNewOTPForSignupFlow (email) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/signup/otp/resend-verification',
                    data: {
                        email
                    }
                }).then(mapResponseBody);
            },
            validateOTP (email, otp, utms) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/login/otp',
                    data: {
                        email,
                        otp,
                        utms
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400,
                            429
                        ])
                }).then((res)=>res.data.authToken);
            },
            validateOTPForSignupFlow (email, otp, utms) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/signup/otp/verify',
                    data: {
                        email,
                        otp,
                        utms
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400
                        ])
                }).then((res)=>res.data.authToken);
            }
        },
        video: {
            markVideoAsWatched (id) {
                return sendRequest({
                    method: 'post',
                    url: "/users/videos/".concat(id, "/watched"),
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            }
        },
        user: {
            switchBusiness (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me/switch-workspace',
                    data: {
                        businessId
                    },
                    disable403Handling: true
                }).then((res)=>res.data.authToken);
            },
            async reorderWorkspaces (workspaceIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me/reorder-workspaces',
                    data: {
                        workspaceIds
                    }
                });
            },
            async setWorkspaceVisibility (businessId, visibility) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me/set-workspace-visibility',
                    data: {
                        businessId,
                        visibility
                    }
                });
            },
            startClientView (clientBusinessId, viewUserId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/me/view-client-portal/".concat(clientBusinessId),
                    urlForSentry: '/user/me/view-client-portal/:clientBusinessId',
                    data: viewUserId != null ? {
                        viewUserId
                    } : undefined
                }).then((res)=>res.data.token);
            },
            exitClientView (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me/exit-view-mode',
                    data: {
                        businessId
                    }
                }).then((res)=>res.data.token);
            },
            // deprecated - endpoint no longer exists - this needs to be removed [ANC-7170]
            createNewClientBusiness (businessName) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/business/customer',
                    data: {
                        name: businessName,
                        accountsPayable: true,
                        accountsReceivable: false
                    }
                }).then(mapResponseBody);
            },
            getUserClientData () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/client-data/user"
                }).then(mapResponseBody);
            },
            async setUserClientData (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/client-data/user",
                    data
                });
            },
            getBusinessClientData () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/client-data/business"
                }).then(mapResponseBody);
            },
            async setBusinessClientData (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/client-data/business",
                    data
                });
            },
            referral () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/user/referral',
                    urlForSentry: '/user/referral'
                }).then(mapResponseBody).then(ReferralModel.fromDTO);
            },
            updateBranding (businessId, branding) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/branding"),
                    urlForSentry: '/business/:id/branding',
                    data: branding
                }).then(mapResponseBody);
            },
            changeUserEmail (param) {
                let { email } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/change-email/otp",
                    data: {
                        email
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.EmailAlreadyRegistered
                        ]) || err.isTooManyAttemptsError()
                }).then(mapResponseBody);
            },
            verifyEmailWithOTP (param) {
                let { otp, email } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/change-email/verify",
                    data: {
                        email,
                        otp
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.EmailAlreadyRegistered,
                            ServerError.InvalidOTP
                        ]) || err.isTooManyAttemptsError()
                }).then(mapResponseBody);
            },
            getOTPCode () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/generic-otp/send",
                    disableSentry: (err)=>err.isTooManyAttemptsError()
                }).then(mapResponseBody);
            },
            verifyOTP (param) {
                let { otp } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/generic-otp/verify",
                    data: {
                        otp
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvalidOTP
                        ]) || err.isTooManyAttemptsError()
                }).then(mapResponseBody);
            },
            getTeamInvites () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/user/team-invites'
                }).then(mapResponseBody).then((res)=>res.invites.map(TeamInviteModel.fromDTO));
            },
            updateBuyer (userId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/".concat(userId, "/update-buyer"),
                    data
                }).then(mapResponseBody);
            },
            updateBuyerEmail (userId, email, customerBusinessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/".concat(userId, "/update-buyer-email"),
                    data: {
                        email,
                        customerBusinessId
                    }
                }).then(mapResponseBody);
            }
        },
        vendor: {
            async createOld (data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/business/vendor',
                    data,
                    disableSentry: (err)=>err.isStripeError() || err.hasServerError(ServerError.ValidationError)
                });
                return res.data;
            },
            async create (data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v3/business/vendor',
                    data: _object_spread_props(_object_spread({}, data), {
                        clientTimestamp: getNow().getTime()
                    })
                });
                return res.data;
            },
            async createV4 (data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v4/business/vendor',
                    data: _object_spread_props(_object_spread({}, data), {
                        clientTimestamp: getNow().getTime()
                    })
                });
                return res.data;
            },
            async updateDetails (data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/business/vendor/update-details',
                    data: _object_spread_props(_object_spread({}, data), {
                        clientTimestamp: getNow().getTime()
                    })
                });
                return res.data;
            },
            async update (data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/business/vendor/update',
                    data,
                    disableSentry: (err)=>err.isStripeError()
                });
                return res.data;
            },
            async authorizeVendorBankAccount (param) {
                let { routingNumber, accountNumber } = param;
                await sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: '/direct/authorize-account',
                    urlForSentry: '/direct/authorize-account',
                    data: {
                        routingNumber,
                        accountNumber
                    }
                });
            },
            async updateBusinessDetails (businessId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/details"),
                    urlForSentry: '/business/:id/details',
                    data
                });
            },
            async updateBusinessSettings (businessId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/settings"),
                    urlForSentry: '/business/:id/settings',
                    data
                });
            },
            async getBusinessSettings (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/settings"),
                    urlForSentry: '/business/:id/settings'
                });
                return BusinessSettingsModel.fromDTO(res.data);
            },
            getStripeHostedKYBUrl (businessId) {
                return sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/stripe-hosted-kyb"),
                    urlForSentry: '/business/:id/stripe-hosted-kyb'
                }).then(mapResponseBody);
            },
            getKYBInquiryWithSelfie (businessId) {
                return sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/inquiry-with-selfie"),
                    urlForSentry: '/business/:id/inquiry-with-selfie'
                }).then(mapResponseBody);
            },
            getKYBUpdatedFlowInquiry (businessId) {
                return sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/inquiry-updated-flow"),
                    urlForSentry: '/business/:id/inquiry-updated-flow'
                }).then(mapResponseBody);
            },
            async getKYBStatus (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/kyb-status"),
                    urlForSentry: '/business/:id/kyb-status'
                });
                return res.data;
            },
            getTaxInfo (businessId) {
                return sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/tax-info"),
                    urlForSentry: '/business/:id/tax-info'
                }).then(mapResponseBody);
            }
        },
        ofacScreening: {
            async createInquiry (data) {
                const res = await sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'post',
                    url: '/ofac-screening-inquiry',
                    data
                });
                return res.data;
            },
            async getUserStatus (userId) {
                const res = await sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/user/".concat(userId, "/ofac-screening-status"),
                    urlForSentry: '/user/:id/ofac-screening-status'
                });
                return new LatestOFACSanctionStatusModel(res.data);
            },
            async getBusinessStatus (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/ofac-screening-status"),
                    urlForSentry: '/business/:id/ofac-screening-status'
                });
                return new LatestOFACSanctionStatusModel(res.data);
            }
        },
        zapier: {
            getApiKey () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/zapier/api-key'
                }).then((res)=>res.data.apiKey);
            }
        },
        member: {
            async getAll (businessId) {
                const bid = businessId || selectors.user.businessId(getState());
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(bid, "/member"),
                    urlForSentry: '/business/:id/member'
                });
                return {
                    members: res.data.members.map((m)=>new MemberModel(m)),
                    accountants: res.data.accountants.map((m)=>new AccountantModel(m))
                };
            },
            async invite (data, businessIdOverride) {
                const businessId = businessIdOverride || selectors.user.businessId(getState());
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/member/invite"),
                    urlForSentry: '/business/:id/member/invite',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.UserAlreadyInBusiness,
                            ServerError.UserAlreadyAffiliatedWithBusiness,
                            ServerError.PendingInvitationAlreadyExists
                        ])
                });
                return res.data;
            },
            async resendInvitation (businessId, invitedUserId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/member/resend-invitation"),
                    urlForSentry: '/business/:id/member/resend-invitation',
                    data: {
                        invitedUserId
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvalidTeamInvitation
                        ])
                });
            },
            async changeRole (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/member/change-role",
                    data
                });
            },
            async remove (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/business/member/deactivate',
                    data
                });
            },
            getAssignedRelationships (memberId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/count/user/".concat(memberId)
                }).then((res)=>res.data.count);
            },
            async reassignAllRelationships (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/relationship/reassign/all',
                    data
                });
            }
        },
        activityLog: {
            async getOld (relationshipId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/activity-log"),
                    urlForSentry: '/relationship/:id/activity-log'
                });
                return res.data.logs.map(ActivityLogEntryModel.make);
            },
            async get (relationshipId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/activity-log/v2"),
                    urlForSentry: '/relationship/:id/activity-log/v2'
                });
                return res.data.logs.map(ActivityLogEntryModel.make);
            },
            async postDiff (payload) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/internal/activity-log-diff',
                    data: payload
                });
            },
            async getPreferences () {
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: '/activity-log-preferences'
                });
                return res.data;
            },
            async savePreferences (input) {
                var _input_updatedAt;
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'post',
                    url: '/activity-log-preferences',
                    data: {
                        showSentNotifications: input.showSentNotifications,
                        updatedAt: (_input_updatedAt = input.updatedAt) !== null && _input_updatedAt !== void 0 ? _input_updatedAt : undefined
                    }
                });
                return res.data;
            }
        },
        invoice: {
            async void (invoiceId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/void"),
                    urlForSentry: '/invoice/:id/void'
                });
            },
            async unvoid (invoiceId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/unvoid"),
                    urlForSentry: '/invoice/:id/unvoid',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceNotVoided
                        ])
                });
            },
            async unmarkAsPaidExternally (invoiceId, options) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/unmark-paid-externally"),
                    urlForSentry: '/invoice/:id/unmark-paid-externally',
                    data: options ? {
                        notificationBehavior: options.notificationBehavior
                    } : undefined,
                    disableSentry: (err)=>err.hasServerError(ServerError.InvoiceNotPaidExternally)
                });
            },
            getByID (invoiceId, isCustomer) {
                return sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/invoice/".concat(invoiceId),
                    urlForSentry: '/invoice/:id',
                    params: {
                        view: isCustomer ? 'customer' : 'vendor'
                    }
                }).then((res)=>invoiceModel.fromLegacyDTO(!isCustomer, res.data));
            },
            getInvoice (invoiceId, isCustomer) {
                return sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/invoices/".concat(invoiceId),
                    urlForSentry: '/invoices/:id',
                    params: {
                        view: isCustomer ? 'customer' : 'vendor'
                    }
                }).then((res)=>invoiceModel.fromDTO(!isCustomer, res.data));
            },
            downloadPDF (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/invoice/".concat(invoiceId, "/pdf"),
                    urlForSentry: '/invoice/:id/pdf'
                }).then((res)=>res.data.url);
            },
            async resend (invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/resend"),
                    urlForSentry: '/invoice/:id/resend'
                });
            },
            async retryCharge (invoiceId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/retry-payment"),
                    urlForSentry: '/invoice/:id/retry-payment'
                });
            },
            async markAsPaidExternally (invoiceId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/paid-externally"),
                    urlForSentry: '/invoice/:id/paid-externally',
                    disableSentry: (err)=>err.hasServerError(ServerError.IncorrectStatusForPaidExternally)
                });
            },
            adhocApprovalLink (invoiceId) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/adhoc-approval-link"),
                    urlForSentry: '/invoice/:id/adhoc-approval-link'
                }).then(mapResponseBody);
            },
            async approve (invoiceId) {
                const isCustomer = selectors.user.isClient(getState());
                if (!isCustomer) throw new Error('only clients can approve invoice');
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/approve"),
                    urlForSentry: '/invoice/:id/approve',
                    params: {
                        isCustomer
                    }
                });
            },
            async getAll () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { relationshipId, sort, search, status, displayStatusV2, paymentStatus, paymentDetails, issuedOnSince, issuedOnUntil, dueDateSince, dueDateUntil, integration, xeroIntegration, pageSize, pageNumber } = filters;
                const isVendor = selectors.user.isVendor(getState());
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoices-summary-with-counters',
                    params: {
                        view: isVendor ? 'vendor' : 'customer',
                        relationship: relationshipId,
                        sort: sort ? "".concat(sort.field, ",").concat(sort.type) : undefined,
                        pageSize,
                        pageNumber,
                        search: search || undefined,
                        status: serializeArray(status),
                        displayStatusV2: serializeArray(displayStatusV2),
                        paymentStatus: serializeArray(paymentStatus),
                        paymentDetails: serializeArray(paymentDetails),
                        issuedOnSince: serializeDate(issuedOnSince),
                        issuedOnUntil: serializeDate(issuedOnUntil),
                        dueDateSince: serializeDate(dueDateSince),
                        dueDateUntil: serializeDate(dueDateUntil),
                        integration: integration === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : filters.integration === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined,
                        xeroIntegration: xeroIntegration === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : filters.xeroIntegration === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined
                    }
                });
                return {
                    invoices: res.data.invoices.map((inv)=>invoiceModel.fromLegacyDTO(isVendor, inv)),
                    total: res.data.total,
                    qboStatusFilterCounters: res.data.qboStatusFilterCounters,
                    xeroStatusFilterCounters: res.data.xeroStatusFilterCounters,
                    displayStatusV2FilterCounters: res.data.displayStatusV2FilterCounters,
                    paymentDetailsFilterCounters: res.data.paymentDetailsFilterCounters
                };
            },
            async getInvoices () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { relationshipId, sort, search, displayStatusV2, paymentDetails, issuedOnSince, issuedOnUntil, dueDateSince, dueDateUntil, integration, xeroIntegration, pageSize, pageNumber } = filters;
                const isVendor = selectors.user.isVendor(getState());
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoices',
                    params: {
                        view: isVendor ? 'vendor' : 'customer',
                        relationshipId,
                        sortField: externalSortField(sort === null || sort === void 0 ? void 0 : sort.field),
                        sortDirection: sort === null || sort === void 0 ? void 0 : sort.type,
                        limit: pageSize,
                        page: pageNumber,
                        search: search || undefined,
                        status: serializeArray(displayStatusV2),
                        paymentDetails: serializeArray(paymentDetails),
                        issuedOnSince: serializeDate(issuedOnSince),
                        issuedOnUntil: serializeDate(issuedOnUntil),
                        dueDateSince: serializeDate(dueDateSince),
                        dueDateUntil: serializeDate(dueDateUntil),
                        qboIntegration: externalSyncFilter(integration),
                        xeroIntegration: externalSyncFilter(xeroIntegration),
                        includeCounters: 'true'
                    }
                });
                const counters = res.data.counters;
                const toLegacySyncCounters = (c)=>{
                    var _ref, _ref1;
                    return {
                        [AccountingSyncStatusFilter.Synced]: (_ref = c === null || c === void 0 ? void 0 : c.synced) !== null && _ref !== void 0 ? _ref : 0,
                        [AccountingSyncStatusFilter.Unsynced]: (_ref1 = c === null || c === void 0 ? void 0 : c.notSynced) !== null && _ref1 !== void 0 ? _ref1 : 0
                    };
                };
                return {
                    invoices: res.data.entries.map((inv)=>invoiceSummaryModel.fromDTO(isVendor, inv)),
                    total: res.data.totalCount,
                    qboStatusFilterCounters: toLegacySyncCounters(counters === null || counters === void 0 ? void 0 : counters.quickbooks),
                    xeroStatusFilterCounters: toLegacySyncCounters(counters === null || counters === void 0 ? void 0 : counters.xero),
                    displayStatusV2FilterCounters: counters === null || counters === void 0 ? void 0 : counters.displayStatus,
                    paymentDetailsFilterCounters: counters === null || counters === void 0 ? void 0 : counters.paymentDetails
                };
            },
            async getInvoicesByCustomer (customerId) {
                const isVendor = selectors.user.isVendor(getState());
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: "/invoices/customer/".concat(customerId),
                    urlForSentry: '/invoices/customer/:customerId'
                });
                return res.data.invoices.map((inv)=>invoiceModel.fromLegacyDTO(isVendor, inv));
            },
            async exportToCsv () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { sort, search, status, paymentStatus, issuedOnSince, issuedOnUntil, integration, view } = filters;
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoices-csv',
                    params: {
                        sort: sort ? "".concat(sort.field, ",").concat(sort.type) : undefined,
                        search: search || undefined,
                        status: serializeArray(status),
                        paymentStatus: serializeArray(paymentStatus),
                        issuedOnSince: serializeDate(issuedOnSince),
                        issuedOnUntil: serializeDate(issuedOnUntil),
                        integration: integration === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : filters.integration === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined,
                        view
                    }
                });
                return res.data;
            },
            async exportToCsvV2 () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { sort, search, displayStatusV2, paymentDetails, issuedOnSince, issuedOnUntil, integration, view } = filters;
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoices-csv-v2',
                    params: {
                        sort: sort ? "".concat(sort.field, ",").concat(sort.type) : undefined,
                        search: search || undefined,
                        displayStatusV2: serializeArray(displayStatusV2),
                        paymentDetails: serializeArray(paymentDetails),
                        issuedOnSince: serializeDate(issuedOnSince),
                        issuedOnUntil: serializeDate(issuedOnUntil),
                        integration: integration === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : filters.integration === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined,
                        view
                    }
                });
                return res.data;
            },
            async stopPayment (invoiceId, reason) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/stop-payment"),
                    data: {
                        reason
                    }
                });
            },
            async resumePayment (invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/resume-payment")
                });
            },
            async refundPayment (invoiceId, reason) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/refund-payment"),
                    data: {
                        reason
                    }
                });
            },
            async refundPartialPayment (invoiceId, data) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/refund"),
                    data
                });
            },
            async calculateRefund (invoiceId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/calculate-refund"),
                    params,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceRefundAmountExceeds,
                            ServerError.InvoiceAlreadyHasOneRefundNotRefunded
                        ])
                }).then(mapResponseBody);
            },
            async payNow (invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/pay-now")
                });
            },
            async attachFiles (invoiceId, attachments) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/attach"),
                    urlForSentry: '/invoice/:id/attach',
                    data: {
                        attachments
                    }
                });
            },
            async getAttachmentDownloadUrl (invoiceId, attachmentId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/invoice/".concat(invoiceId, "/attachments/").concat(attachmentId, "/download"),
                    urlForSentry: '/invoice/:id/attachments/:attachmentId/download'
                });
                return res.data.url;
            },
            async removeAttachment (invoiceId, attachmentId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'delete',
                    url: "/invoice/".concat(invoiceId, "/attachments/").concat(attachmentId),
                    urlForSentry: '/invoice/:id/attachments/:attachmentId'
                });
            },
            disableAutoApprove (invoiceID) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    urlForSentry: '/invoice/:id/auto-approve/disable',
                    method: 'post',
                    url: "/invoice/".concat(invoiceID, "/auto-approve/disable")
                }).then(mapResponseBody);
            },
            getDigestFromQuery () {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/invoices/vendor/".concat(businessId, "/payment-status-counts"),
                    urlForSentry: "/invoices/vendor/:id/payment-status-counts"
                }).then((response)=>InvoicesDigestModel.fromOptimizedQueryDTO(response.data));
            },
            approveInvoiceFromEmail (param) {
                let { approvalToken } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/approve-by-token",
                    data: {
                        approvalToken
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceAlreadyApproved,
                            ServerError.InvoiceWasVoided,
                            ServerError.InvalidRelationshipStatus
                        ])
                }).then(mapResponseBody);
            },
            approveAdHoc (param) {
                let { invoiceId, fundingSourceId, approvedLegalTermsId } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/approve-adhoc"),
                    urlForSentry: '/invoice/:invoiceId/approve-adhoc',
                    data: {
                        fundingSourceId,
                        approvedLegalTermsId
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceAlreadyApproved,
                            ServerError.InvoiceWasVoided,
                            ServerError.InvalidRelationshipStatus
                        ])
                }).then(mapResponseBody);
            },
            retryAdHocPayment (param) {
                let { invoiceId, fundingSourceId } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/retry-adhoc-payment"),
                    urlForSentry: '/invoice/:invoiceId/retry-adhoc-payment',
                    data: {
                        fundingSourceId
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceAlreadyApproved,
                            ServerError.InvoiceWasVoided,
                            ServerError.InvalidRelationshipStatus
                        ])
                }).then(mapResponseBody);
            },
            resyncQbo (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/qbo/invoices/".concat(invoiceId, "/resync"),
                    urlForSentry: "/integrations/accounting/qbo/invoices/:id/resync"
                }).then(mapResponseBody);
            },
            resyncXero (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/xero/invoices/".concat(invoiceId, "/resync"),
                    urlForSentry: "/integrations/accounting/xero/invoices/:id/resync"
                }).then(mapResponseBody);
            },
            getResyncXeroStatus (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/xero/invoices/".concat(invoiceId, "/resync-status"),
                    urlForSentry: "/integrations/accounting/xero/invoices/:invoiceId/resync-status"
                }).then(mapResponseBody);
            },
            getResyncQboStatus (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/qbo/invoices/".concat(invoiceId, "/resync-status"),
                    urlForSentry: "/integrations/accounting/qbo/invoices/:invoiceId/resync-status"
                }).then(mapResponseBody);
            },
            async getMetrics (vendorId) {
                let filters = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const mapAccountingFilter = (val)=>val === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : val === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: "/invoices/vendor/".concat(vendorId, "/metrics"),
                    urlForSentry: '/invoices/vendor/:vendorId/metrics',
                    params: {
                        issuedOnSince: serializeDate(filters.issuedOnSince),
                        issuedOnUntil: serializeDate(filters.issuedOnUntil),
                        displayStatusV2: serializeArray(filters.displayStatusV2),
                        paymentStatus: serializeArray(filters.paymentStatus),
                        paymentDetails: serializeArray(filters.paymentDetails),
                        search: filters.search || undefined,
                        relationship: filters.relationship || undefined,
                        integration: mapAccountingFilter(filters.integration),
                        xeroIntegration: mapAccountingFilter(filters.xeroIntegration),
                        dueDateSince: serializeDate(filters.dueDateSince),
                        dueDateUntil: serializeDate(filters.dueDateUntil)
                    }
                });
                return res.data;
            },
            async getDashboardPreferences () {
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoice-dashboard-preferences'
                });
                return res.data;
            },
            async saveDashboardPreferences (input) {
                var _input_updatedAt;
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/invoice-dashboard-preferences',
                    data: {
                        cards: input.cards,
                        updatedAt: (_input_updatedAt = input.updatedAt) !== null && _input_updatedAt !== void 0 ? _input_updatedAt : undefined
                    }
                });
                return res.data;
            }
        },
        serviceTemplate: {
            getAll (businessId, param) {
                let { includeDeleted, includeAdhocCharges, invoiceId, type } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/service-template"),
                    urlForSentry: "/business/:id/service-template",
                    params: {
                        includeDeleted,
                        includeAdhocCharges,
                        invoiceId,
                        type: type.join(',')
                    }
                }).then((res)=>{
                    const { services } = res.data;
                    const raw = services.map(serviceTemplateModel.fromDTO);
                    const active = [];
                    const deleted = [];
                    raw.forEach((s)=>{
                        if (serviceTemplateModel.isDeleted(s)) {
                            deleted.push(s);
                        } else {
                            active.push(s);
                        }
                    });
                    return {
                        active,
                        deleted,
                        raw
                    };
                });
            },
            getById (businessId, serviceTemplateId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/service-template/").concat(serviceTemplateId),
                    urlForSentry: '/business/:id/service-template/:serviceId'
                }).then((res)=>serviceTemplateModel.fromDTO(res.data));
            },
            create (serviceTemplate, businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/service-template/create"),
                    urlForSentry: 'v2/business/:id/service-template/create',
                    data: serviceTemplateModel.toDTO(serviceTemplate),
                    disableSentry: (err)=>err.hasServerError(ServerError.ServiceTemplateAlreadyExists)
                }).then(mapResponseBody);
            },
            update (serviceTemplate, businessId, serviceContentObj, descriptionObj) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/service-template/update"),
                    urlForSentry: '/v2/business/:id/service-template/update',
                    data: _object_spread({}, serviceTemplateModel.toDTO(serviceTemplate), serviceContentObj && {
                        serviceContentObj
                    }, descriptionObj && {
                        descriptionObj
                    }),
                    disableSentry: (err)=>err.hasServerError(ServerError.ServiceTemplateAlreadyExists)
                }).then(()=>{});
            },
            async delete (id, businessId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/service-template/delete"),
                    urlForSentry: "/business/:id/service-template/delete",
                    data: {
                        id
                    }
                });
            },
            async exportServiceTemplatesToCsv (businessId, templateType) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/service-template/export-csv"),
                    urlForSentry: '/business/:id/service-template/export-csv',
                    params: {
                        type: templateType
                    }
                });
                return res.data;
            }
        },
        integration: {
            async getAll () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/list',
                    params: {
                        lightweight: false
                    }
                });
                return res.data.map(integrationModel.fromDTO);
            },
            async getSummary () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/list',
                    params: {
                        lightweight: true
                    }
                });
                return res.data.map(integrationModel.fromDTO);
            }
        },
        accounting: {
            async connect (param) {
                let { redirectUrl, accounting } = param;
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/connect"),
                    data: {
                        redirectUrl
                    }
                });
                return res.data.linkUrl;
            },
            disconnect (param) {
                let { accounting } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/disconnect")
                }).then(mapResponseBody);
            },
            getAccounts (param) {
                let { accounting } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/".concat(accounting, "/accounts")
                }).then(mapResponseBody);
            },
            getCustomers (param) {
                let { accounting } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/".concat(accounting, "/customers")
                }).then((res)=>res.data.customers.map(AccountingCustomerModel.fromDTO));
            },
            getItems (param) {
                let { accounting, type } = param;
                return sendRequest({
                    method: 'get',
                    apiHost: APIHost.Mgmt,
                    url: "/integrations/accounting/".concat(accounting, "/items?type=").concat(type.join(','))
                }).then((res)=>_object_spread_props(_object_spread({}, res.data), {
                        items: res.data.items.map((item)=>{
                            var _item_type;
                            return _object_spread_props(_object_spread({}, item), {
                                type: (_item_type = item.type) !== null && _item_type !== void 0 ? _item_type : AccountingItemType.Service
                            });
                        })
                    }));
            },
            async linkAccount (param) {
                let { data, accounting } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/link-account"),
                    data
                });
            },
            async linkServiceTemplate (param) {
                let { serviceTemplateId, integratedId, accounting, type } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/service-templates/").concat(serviceTemplateId, "/link"),
                    urlForSentry: "/integrations/accounting/:id/service-templates/:id/link",
                    data: {
                        integratedId,
                        type
                    }
                });
            },
            async createContact (param) {
                let { contactId, accounting } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/clients/").concat(contactId, "/create"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/clients/:id/create"),
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.DuplicateNameExists,
                            ServerError.QboSubscriptionInactive
                        ])
                }).then(mapResponseBody);
            },
            async linkContact (param) {
                let { contactId, data, accounting } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/clients/").concat(contactId, "/link"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/clients/:id/link"),
                    data
                });
            },
            async syncInvoice (accounting, invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/invoices/").concat(invoiceId),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/invoices/:id")
                });
            },
            async retrySyncInvoice (accounting, invoiceId, includeTimeActivities) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/invoices/").concat(invoiceId, "/retry"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/invoices/:id/retry"),
                    data: {
                        includeTimeActivities
                    }
                });
            },
            async syncRefund (accounting, invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/invoices/").concat(invoiceId, "/refund"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/invoices/:id/refund")
                });
            },
            async syncPayment (param) {
                let { invoiceId, accounting } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/invoices/").concat(invoiceId, "/payment"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/invoices/:id/payment")
                });
            },
            async syncPayout (param) {
                let { payoutId, accounting } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/payouts/").concat(payoutId)
                });
            },
            async updateSettings (param) {
                let { accounting, settings } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/update"),
                    data: settings
                });
            }
        },
        qbo: {
            async getClasses () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/qbo/classes"
                });
                return res.data.classes;
            },
            async getTimeActivities (relationshipId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/qbo/relationships/".concat(relationshipId, "/time-activities"),
                    urlForSentry: '/integrations/accounting/qbo/relationships/:id/time-activities'
                });
                return TimeActivitiesModel.fromDTO(res.data.services);
            }
        },
        practiceManagement: {
            async getMatchingCompanies (pmName, relationshipId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/".concat(pmName, "/relationships/").concat(relationshipId, "/matched-contacts-companies"),
                    urlForSentry: "/integrations/".concat(pmName, "/relationships/:relationshipId/matched-contacts-companies")
                });
                return res.data.companies || [];
            },
            async linkContact (pmName, relationshipId, companyId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/".concat(pmName, "/relationships/").concat(relationshipId, "/link-contact"),
                    urlForSentry: "/integrations/".concat(pmName, "/relationships/:id/link-contact"),
                    data: {
                        companyId: companyId
                    }
                }).then(mapResponseBody);
            },
            async createNewCompanyAndContact (pmName, relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/".concat(pmName, "/relationships/").concat(relationshipId, "/create-client"),
                    urlForSentry: "/integrations/".concat(pmName, "/relationships/:id/create-client")
                }).then(mapResponseBody);
            },
            async dismissSyncFailure (pmName, relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/".concat(pmName, "/relationships/").concat(relationshipId, "/dismiss-failure"),
                    urlForSentry: "/integrations/".concat(pmName, "/relationships/:id/dismiss-failure")
                }).then(mapResponseBody);
            }
        },
        financialCents: {
            async connect (redirectUrl) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/financial-cents',
                    data: {
                        type: 'oauth',
                        oauth: {
                            redirectUrl
                        }
                    }
                });
                return res.data.oauth.linkUrl;
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/financial-cents/disconnect'
                });
            },
            async getItems () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/financial-cents/items'
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.items : [];
            },
            async linkServiceTemplate (serviceTemplateId, integratedIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/financial-cents/items/link',
                    data: {
                        id: serviceTemplateId,
                        integratedIds
                    }
                });
            },
            async getUsers () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/financial-cents/users'
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.users || [] : [];
            },
            async getRoles (serviceTemplateId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/financial-cents/".concat(serviceTemplateId, "/roles")
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.roles || [] : [];
            }
        },
        serviceInvoices: {
            getByBusinessId (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/serviceBilling/".concat(businessId),
                    urlForSentry: '/serviceBilling/:businessId'
                }).then((res)=>(res.data.Entries || []).map(ServiceInvoiceModel.fromDTO));
            },
            getCredit () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/business/payments-credit'
                }).then((res)=>utils.centsToDollars(res.data.amount));
            },
            getDownloadURL (filename) {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/file/").concat(filename),
                    urlForSentry: '/business/:id/file/:id'
                }).then(mapResponseBody);
            }
        },
        paymentMethod: {
            /**
             * Fetches the payment methods for a given agreement
             * When the vendor sends the request he will get an empty array.
             */ clientGet (vendorId) {
                return this.clientGetWithContext(vendorId).then((param)=>{
                    let { paymentMethods } = param;
                    return paymentMethods;
                });
            },
            /**
             * Same as clientGet but also surfaces `lastUsedFundingSourceId` so callers
             * that render a pre-selection UI (e.g. the ad-hoc invoice approval page) can
             * pre-select the most recently used payment method.
             */ clientGetWithContext (vendorId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/payment-method/vendor/".concat(vendorId),
                    urlForSentry: '/payment-method/vendor/:id'
                }).then((res)=>({
                        paymentMethods: res.data.paymentMethods,
                        lastUsedFundingSourceId: res.data.lastUsedFundingSourceId
                    }));
            },
            /**
             * Fetches the payment methods for a given agreement
             * When the client sends the request he will get an empty array.
             */ vendorGet (customerId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/payment-method/customer/".concat(customerId),
                    urlForSentry: '/payment-method/customer/:id'
                }).then((res)=>res.data.paymentMethods);
            },
            clientAddToBusiness (paymentMethodId, vendorId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/".concat(paymentMethodId, "/vendor/").concat(vendorId),
                    urlForSentry: '/payment-method/:id/vendor/:id'
                }).then((res)=>res.data.details);
            },
            vendorAddToBusiness (paymentMethodId, customerId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/".concat(paymentMethodId, "/customer/").concat(customerId),
                    urlForSentry: '/payment-method/:id/customer/:id'
                }).then((res)=>res.data.details);
            },
            async delete (fundingSourceId, vendorId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/funding-source/".concat(fundingSourceId, "/delete"),
                    urlForSentry: '/payment-method/funding-source/delete',
                    data: {
                        vendorId
                    }
                });
            },
            async clientDelete (fundingSourceId, vendorId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/funding-source/".concat(fundingSourceId, "/by-customer/delete"),
                    urlForSentry: '/payment-method/funding-source/:id/by-customer/delete',
                    data: {
                        vendorId
                    }
                });
            },
            async vendorDelete (fundingSourceId, customerId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/funding-source/".concat(fundingSourceId, "/by-vendor/delete"),
                    urlForSentry: '/payment-method/funding-source/:id/by-vendor/delete',
                    data: {
                        customerId
                    }
                });
            },
            createSetupIntent (vendorId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/".concat(vendorId, "/create-payment-intent"),
                    urlForSentry: '/payment-method/:vendorId/create-payment-intent'
                }).then(mapResponseBody);
            },
            clientCreateSetupIntent (vendorId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/vendor/".concat(vendorId, "/create-payment-intent"),
                    urlForSentry: '/payment-method/vendor/:vendorId/create-payment-intent'
                }).then(mapResponseBody);
            },
            vendorCreateSetupIntent (customerId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/customer/".concat(customerId, "/create-payment-intent"),
                    urlForSentry: '/payment-method/customer/:customerId/create-payment-intent'
                }).then(mapResponseBody);
            }
        },
        business: {
            getLegalTerms () {
                const businessId = selectors.user.businessId(getState());
                if (!businessId) throw new Error('no business ID');
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/legal-terms"),
                    urlForSentry: '/business/:id/legal-terms'
                }).then(mapResponseBody);
            },
            async removeLegalTerms (legalTermsId) {
                const businessId = selectors.user.businessId(getState());
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/legal-terms/delete"),
                    urlForSentry: '/business/:id/legal-terms/delete',
                    data: {
                        id: legalTermsId
                    }
                });
            },
            async renameLegalTerms (legalTermsId, newName) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/".concat(legalTermsId, "/rename"),
                    urlForSentry: '/v2/business/legal-terms/:legalTermsId/rename',
                    data: {
                        name: newName
                    }
                }).then(mapResponseBody);
            },
            createLegalTermsOld (data) {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/legal-terms/create"),
                    urlForSentry: '/business/:id/legal-terms/create',
                    data
                }).then(mapResponseBody);
            },
            createLegalTerms (data) {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/legal-terms/create"),
                    urlForSentry: '/v2/business/:id/legal-terms/create',
                    data
                }).then(mapResponseBody);
            },
            createQuillLegalTerms (data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/quill",
                    urlForSentry: '/v2/business/legal-terms/quill',
                    data
                }).then(mapResponseBody);
            },
            updateQuillLegalTerms (legalTermsId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/quill/".concat(legalTermsId),
                    urlForSentry: '/v2/business/legal-terms/quill/:legalTermsId',
                    data
                }).then(mapResponseBody);
            },
            getLegalTermsUploadURL () {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/legal-terms/upload-url"),
                    urlForSentry: '/business/:id/legal-terms/upload-url'
                }).then(mapResponseBody);
            },
            getLegalTermsDownloadURL (filename, businessId) {
                const actualBusinessId = businessId || selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(actualBusinessId, "/legal-terms/download-url"),
                    urlForSentry: '/business/:id/legal-terms/download-url',
                    params: {
                        filename
                    }
                }).then((res)=>res.data.url);
            },
            getLogoUploadURL () {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/logo/upload-url"),
                    urlForSentry: '/business/:id/logo/upload-url'
                }).then((res)=>res.data.url);
            },
            getUploadUrls (filesData) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/upload-urls",
                    urlForSentry: '/business/upload-urls',
                    data: {
                        filesData
                    }
                }).then(mapResponseBody);
            },
            getInfo (businessId) {
                return sendRequest({
                    method: 'get',
                    url: "/businesses/".concat(businessId),
                    urlForSentry: '/businesses/:id',
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            },
            updateBusinessOnboardingWidgetState (businessId, onboardingWidgetState) {
                return sendRequest({
                    method: 'post',
                    url: "/businesses/".concat(businessId, "/onboarding-state"),
                    urlForSentry: '/businesses/:id/onboarding-state',
                    apiHost: APIHost.Scout,
                    data: {
                        onboardingWidgetState
                    }
                }).then(mapResponseBody);
            },
            async acceptTeamInvite (param) {
                let { invitationId, businessId } = param;
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/member/accept-invite"),
                    data: {
                        invitationId
                    },
                    urlForSentry: "/business/:businessId/member/accept-invite",
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvalidTeamInvitation
                        ])
                });
                return res.data;
            },
            async getAPIKeys (params) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'get',
                    url: '/business/api-keys',
                    urlForSentry: '/business/api-keys',
                    params: {
                        sortField: params === null || params === void 0 ? void 0 : params.sortBy,
                        sortDirection: params === null || params === void 0 ? void 0 : params.sortDirection
                    }
                }).then(mapResponseBody);
            },
            async createAPIKey (name) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: '/business/api-key',
                    data: {
                        name
                    },
                    urlForSentry: '/business/api-key'
                }).then(mapResponseBody);
            },
            async regenerateAPIKey (id) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/business/api-key/regenerate",
                    urlForSentry: '/business/api-key/regenerate',
                    data: {
                        id
                    }
                }).then(mapResponseBody);
            },
            async renameAPIKey (param) {
                let { id, newName } = param;
                await sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/business/api-key/rename",
                    urlForSentry: '/business/api-key/rename',
                    data: {
                        id,
                        newName
                    },
                    disableSentry: (e)=>e.hasServerError([
                            ServerError.APIKeyNotFound,
                            ServerError.APIKeyRenameFailed
                        ])
                });
            },
            async deactivateAPIKey (id) {
                await sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/business/api-key/deactivate",
                    urlForSentry: '/business/api-keys/deactivate',
                    data: {
                        id
                    }
                });
            },
            updateBuyerCompany (customerBusinessId, companyName) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(customerBusinessId, "/update-client"),
                    data: {
                        companyName
                    }
                }).then(mapResponseBody);
            },
            async getActivityLogs () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/activity-log",
                    urlForSentry: '/business/activity-log',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.ClientNotOwnedByVendor,
                            ServerError.MultiVendorCustomerNotEditable
                        ])
                });
                return res.data.logs.map(ActivityLogEntryModel.make);
            }
        },
        invoiceDraft: {
            getDates (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/billing/".concat(relationshipId, "/dates"),
                    urlForSentry: '/billing/:id/dates'
                }).then((res)=>InvoiceDraftDatesModel.fromDTO(res.data));
            },
            getByDate (relationshipId, date) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/billing/".concat(relationshipId, "/invoice-draft"),
                    urlForSentry: '/billing/:id/invoice-draft',
                    params: {
                        issueDate: timeModel.toDatestampArray(date).join(',')
                    }
                }).then((res)=>res.data && invoiceDraftModel.fromDTO(res.data));
            },
            getForAmendedService (relationshipId, service) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/amended-item-draft"),
                    urlForSentry: '/relationship/:id/amended-item-draft',
                    data: serviceModel.toDTO(service)
                }).then((res)=>({
                        issueDate: fromDateStampObject(res.data.issueDate),
                        lineItem: invoiceDraftModel.lineItemFromDTO(res.data.lineItem)
                    }));
            }
        },
        notification: {
            emailOnBehalf: {
                async connectEmailOnBehalf (param) {
                    let { businessId, provider, pageUrl } = param;
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/business/".concat(businessId, "/settings/notifications/send-on-behalf-of/authorize-").concat(provider),
                        urlForSentry: "/business/:businessId/settings/notifications/send-on-behalf-of/authorize-".concat(provider),
                        config: {
                            headers: {
                                'anchor-return-to-path': pageUrl
                            }
                        }
                    }).then((response)=>response.data.url);
                },
                async completeGmailAuthorization (param) {
                    let { businessId, code, scope, csrfToken, redirectUri } = param;
                    await sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/business/".concat(businessId, "/settings/notifications/send-on-behalf-of/complete-gmail-authorization"),
                        urlForSentry: "/business/:businessId/settings/notifications/send-on-behalf-of/complete-gmail-authorization",
                        data: {
                            authorizationCode: code,
                            scope,
                            csrfToken,
                            redirectUri
                        },
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.InvalidScopeMissingGmailSend
                            ])
                    });
                },
                async completeOutlookAuthorization (param) {
                    let { businessId, code, csrfToken, redirectUri } = param;
                    await sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/business/".concat(businessId, "/settings/notifications/send-on-behalf-of/complete-outlook-authorization"),
                        urlForSentry: "/business/:businessId/settings/notifications/send-on-behalf-of/complete-outlook-authorization",
                        data: {
                            authorizationCode: code,
                            csrfToken,
                            redirectUri
                        }
                    });
                },
                async disconnectEmailOnBehalf (businessId) {
                    await sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'delete',
                        url: "/business/".concat(businessId, "/settings/notifications/send-on-behalf-of"),
                        urlForSentry: "/business/:businessId/settings/notifications/send-on-behalf-of"
                    });
                }
            },
            async getWhileLoggedIn (notificationId, intent) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    params: {
                        intent
                    },
                    url: "/notification/auth/".concat(notificationId),
                    urlForSentry: '/notification/auth/:id',
                    disable403Handling: true,
                    disableSentry: (err)=>err.status === 401 || err.hasServerError([
                            ServerError.RecipientNotFound,
                            ServerError.InvalidTeamInvitation
                        ])
                });
                return res.data;
            },
            async getWhileLoggedOut (notificationId, intent) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    params: {
                        intent
                    },
                    url: "/notification/no-auth/".concat(notificationId),
                    urlForSentry: '/notification/no-auth/:id',
                    disableSentry: (err)=>err.hasStatus([
                            401
                        ]),
                    disable403Handling: true
                });
                return res.data;
            },
            async getNotificationSettings (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/settings/notifications"),
                    urlForSentry: "/business/:businessId/settings/notifications"
                }).then((res)=>_object_spread_props(_object_spread({}, res.data), {
                        sendOnBehalfOf: EmailOnBehalfSettingsModel.fromDTO(res.data.sendOnBehalfOf)
                    }));
            },
            async updateNotificationSettings (param) {
                let { data, businessId, notificationName } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/settings/notifications/").concat(notificationName),
                    urlForSentry: "/business/:businessId/settings/notifications/:notificationType",
                    data
                });
            },
            async updateNotificationSettingsV2 (param) {
                let { data, businessId, notificationName } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/settings/notifications/").concat(notificationName),
                    urlForSentry: "/v2/business/:businessId/settings/notifications/:notificationType",
                    data
                });
            },
            async setNotificationStatusV2 (param) {
                let { businessId, notificationName, status } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/settings/notifications/").concat(notificationName, "/status"),
                    urlForSentry: "/v2/business/:businessId/settings/notifications/:notificationType/status",
                    data: {
                        status
                    }
                });
            },
            async revertNotificationSetting (param) {
                let { businessId, notificationName } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/settings/notifications/").concat(notificationName, "/revert"),
                    urlForSentry: "/v2/business/:businessId/settings/notifications/:notificationType/revert"
                });
            }
        },
        inAppNotifications: {
            getAll () {
                return sendRequest({
                    method: 'get',
                    apiHost: APIHost.Mgmt,
                    url: '/business/notifications-mailbox'
                }).then(mapResponseBody);
            },
            async markAsSeen (notificationItemIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/notifications-mailbox/mark-as-seen",
                    data: {
                        ids: notificationItemIds
                    }
                });
            },
            async markAsRead (notificationItemId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/notifications-mailbox/mark-as-read",
                    data: {
                        id: notificationItemId
                    }
                });
            }
        },
        accountants: {
            /** Request access as an accountant to the customer's business. */ async requestAccess (customerId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/accountants/request-access/".concat(customerId),
                    urlForSentry: '/business/accountants/request-access/:id'
                }).then(mapResponseBody);
            },
            /** Cancels an active request for access. */ async cancelRequestAccess (customerId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/accountants/cancel-request-access/".concat(customerId),
                    urlForSentry: '/business/accountants/cancel-request-access/:id'
                });
            },
            /** Removes access for the existing user as an accountant to the customer's business. */ async removeMe (customerId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/accountants/remove-me/".concat(customerId),
                    urlForSentry: '/business/accountants/remove-me/:id'
                });
            },
            async removeVendor (vendorId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/accountants/remove/".concat(vendorId),
                    urlForSentry: '/business/accountants/remove/:id'
                });
            },
            async approveVendor (vendorId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/accountants/approve/".concat(vendorId),
                    urlForSentry: '/business/accountants/approve/:id'
                });
            },
            async rejectVendor (vendorId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/accountants/reject/".concat(vendorId),
                    urlForSentry: '/business/accountants/reject/:id'
                });
            }
        },
        payout: {
            getById (payoutId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/v2/business/payouts/".concat(payoutId),
                    urlForSentry: '/v2/business/payouts/:id'
                }).then((res)=>PayoutModel.fromDTO(res.data));
            },
            async getAllFromScout () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { qboIntegration, xeroIntegration, pageSize, pageNumber, sort, search } = filters;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/payouts-summary-with-counters',
                    params: {
                        sortField: sort === null || sort === void 0 ? void 0 : sort.field,
                        sortDirection: sort === null || sort === void 0 ? void 0 : sort.type,
                        pageSize,
                        pageNumber,
                        search,
                        qboSyncStatus: qboIntegration,
                        xeroSyncStatus: xeroIntegration
                    }
                });
                return {
                    payouts: res.data.payouts.map((payout)=>PayoutModel.fromScoutDTO(payout)),
                    invoiceHeaders: res.data.invoiceHeaders,
                    total: res.data.total,
                    qboSyncStatusFilterCounters: res.data.qboSyncStatusFilterCounters,
                    xeroSyncStatusFilterCounters: res.data.xeroSyncStatusFilterCounters
                };
            },
            async getAllFromExternalApi () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                var _res_data_counters, _res_data_counters1;
                const { qboIntegration, xeroIntegration, pageSize, pageNumber, sort, search } = filters;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/payouts',
                    params: {
                        page: pageNumber,
                        limit: pageSize,
                        search,
                        sortField: sort === null || sort === void 0 ? void 0 : sort.field,
                        sortDirection: sort === null || sort === void 0 ? void 0 : sort.type,
                        qboIntegration,
                        xeroIntegration,
                        includeCounters: 'true'
                    }
                });
                return {
                    payouts: res.data.entries.map((payout)=>PayoutModel.fromExternalDTO(payout)),
                    invoiceHeaders: EXTERNAL_PAYOUT_INVOICE_HEADERS,
                    total: res.data.totalCount,
                    qboSyncStatusFilterCounters: (_res_data_counters = res.data.counters) === null || _res_data_counters === void 0 ? void 0 : _res_data_counters.quickbooks,
                    xeroSyncStatusFilterCounters: (_res_data_counters1 = res.data.counters) === null || _res_data_counters1 === void 0 ? void 0 : _res_data_counters1.xero
                };
            },
            async getPayoutInvoices (payoutId) {
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: "/payouts/".concat(payoutId, "/invoices"),
                    urlForSentry: '/payouts/:id/invoices'
                });
                return res.data.entries.map(PayoutModel.fromExternalInvoiceDTO);
            },
            async getSummary () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/business/payouts-summary',
                    params: {
                        limit: 5,
                        withFuturePayouts: 'true'
                    }
                });
                return PayoutsSummaryModel.fromDTO(res.data);
            },
            async exportPayoutsToCsv () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { qboIntegration, xeroIntegration, sort, search } = filters;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/payouts-csv',
                    params: {
                        sortField: sort === null || sort === void 0 ? void 0 : sort.field,
                        sortDirection: sort === null || sort === void 0 ? void 0 : sort.type,
                        search,
                        qboSyncStatus: qboIntegration,
                        xeroSyncStatus: xeroIntegration
                    }
                });
                return res.data;
            }
        },
        report: {
            getMonthlyEarnings () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/business/report/monthly-earnings'
                }).then(mapResponseBody);
            },
            async getHistoricAgreementStats (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/businesses/".concat(businessId, "/historic-agreements-stats"),
                    urlForSentry: '/businesses/:id/historic-agreements-stats'
                });
                return res.data.map((stats)=>new HistoricAgreementStatsModel(stats));
            },
            async getSalesSummary () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/business/sales-summary'
                });
                return SalesSummaryModel.fromDTO(res.data, getNow());
            },
            async getHistoricPaidExternallyInvoices () {
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/historic-paid-externally-invoices"
                });
                return res.data.map(HistoricRevenueModel.fromHistoricInvoiceDTO);
            },
            async getFutureInvoiceTotals (param) {
                let { to } = param;
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/future-invoices/totals",
                    params: {
                        to
                    }
                });
                return res.data.map(HistoricRevenueModel.fromHistoricInvoiceDTO);
            },
            async getIssuedTotalAmount (vendorId) {
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/invoices/vendor/".concat(vendorId, "/issued-total-amount")
                });
                return res.data.totalAmount;
            }
        },
        featureFlag: {
            getAll (businessId, authToken) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/feature-flags/fe',
                    params: {
                        businessId
                    },
                    authToken
                }).then(mapResponseBody);
            }
        },
        plaid: {
            getLinkToken (mode) {
                let { fundingSourceId, relationshipId } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                return sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: '/link-token',
                    data: {
                        mode,
                        fundingSourceId: fundingSourceId || null,
                        relationshipId
                    }
                }).then((response)=>response.data.token);
            },
            exchangePublicToken (body) {
                return sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: '/exchange-public-token',
                    data: body,
                    disableSentry: (err)=>err.isPlaidError()
                }).then(mapResponseBody);
            },
            async verifyMicroDeposit (fundingSourceId) {
                await sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: "/verify/".concat(fundingSourceId),
                    urlForSentry: '/verify/:fundingSourceId'
                });
            },
            async failedVerification (fundingSourceId) {
                await sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: "/failed-verification/".concat(fundingSourceId),
                    urlForSentry: '/verify-failed/:fundingSourceId'
                });
            },
            isLoginRequired (fundingSourceId) {
                return sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: "/funding-source/".concat(fundingSourceId, "/is-login-required"),
                    urlForSentry: '/funging-source/:fundingSourceId/is-login-required'
                }).then((res)=>res.data.isLoginRequired);
            },
            async loginRepaired (fundingSourceId) {
                await sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: "/funding-source/".concat(fundingSourceId, "/login-repaired"),
                    urlForSentry: '/funging-source/:fundingSourceId/login-repaired'
                });
            }
        },
        proposalTemplates: {
            async getAll () {
                if (!selectors.user.isVendor(getState())) {
                    // Endpoint requires the Vendor role; non-vendors get 403.
                    return [];
                }
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/proposal-templates/all",
                    disable403Handling: true
                }).then(mapResponseBody).then((templates)=>templates.map(ProposalTemplateSummaryModel.fromDTO));
            },
            async get (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/proposal-templates/".concat(id),
                    urlForSentry: '/proposal-templates/:id'
                }).then(mapResponseBody).then(proposalTemplateModel.fromDTO);
            },
            async save (id, template) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(id),
                    urlForSentry: '/proposal-templates/:id',
                    data: template,
                    disableSentry: (err)=>err.hasServerError(ServerError.NameAlreadyExists)
                }).then(mapResponseBody);
            },
            async rename (id, title) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(id, "/rename"),
                    urlForSentry: '/proposal-templates/:id/rename',
                    data: {
                        title
                    },
                    disableSentry: (err)=>err.hasServerError(ServerError.NameAlreadyExists)
                }).then(mapResponseBody);
            },
            async remove (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(id, "/delete"),
                    urlForSentry: '/proposal-templates/:id/delete'
                }).then(mapResponseBody);
            },
            async removeV2 (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'delete',
                    url: "/v2/proposal-templates/".concat(id),
                    urlForSentry: '/v2/proposal-templates/:id'
                }).then(mapResponseBody);
            },
            async create (template) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/create",
                    urlForSentry: '/proposal-templates/create',
                    data: template,
                    disableSentry: (err)=>err.hasServerError(ServerError.NameAlreadyExists)
                }).then(mapResponseBody);
            },
            async createDraft (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(id, "/create-relationship-draft"),
                    urlForSentry: '/proposal-templates/:id/create-relationship-draft'
                }).then(mapResponseBody);
            },
            async createFromDraft (id, title) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/create-from-draft/".concat(id),
                    urlForSentry: '/proposal-templates/create-from-draft/:id',
                    disableSentry: (err)=>err.hasServerError(ServerError.NameAlreadyExists),
                    data: {
                        title
                    }
                }).then(mapResponseBody);
            },
            getBillingSchedule (templateId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/proposal-templates/".concat(templateId, "/billing-schedule"),
                    urlForSentry: '/proposal-templates/:id/billing-schedule',
                    params
                }).then(mapResponseBody);
            }
        },
        reports: {
            async revenueReportServicesV3 () {
                return sendRequest({
                    method: 'get',
                    url: "/revenue-report/services/v3",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async revenueReportCustomersV3 () {
                return sendRequest({
                    method: 'get',
                    url: "/revenue-report/customers/v3",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async revenueReportV3 (param) {
                let { start, end, breakdowns, aggregate, services, customers, sort } = param;
                const params = {
                    start,
                    end,
                    breakdowns,
                    aggregate,
                    sort
                };
                const queryParams = Object.entries(params).filter((param)=>{
                    let [, value] = param;
                    return !!value;
                }).map((param)=>{
                    let [key, value] = param;
                    return "".concat(key, "=").concat(value);
                }).join('&');
                return sendRequest({
                    method: 'post',
                    url: "/revenue-report/v3".concat(queryParams !== '' ? "?".concat(queryParams) : ''),
                    apiHost: APIHost.Scout,
                    data: {
                        services: services.length > 0 ? services : undefined,
                        customers: customers.length > 0 ? customers : undefined
                    }
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReportServices () {
                return sendRequest({
                    method: 'get',
                    url: "/active-services-report/services",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReportCustomers () {
                return sendRequest({
                    method: 'get',
                    url: "/active-services-report/customers",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReportAgreements () {
                return sendRequest({
                    method: 'get',
                    url: "/active-services-report/agreements",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReportAssignees () {
                return sendRequest({
                    method: 'get',
                    url: "/active-services-report/assignees",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReport (param) {
                let { businessId, params, services, customers, agreements, assignees } = param;
                const queryParams = Object.entries(params).filter((param)=>{
                    let [, value] = param;
                    return value !== undefined;
                }).map((param)=>{
                    let [key, value] = param;
                    return Array.isArray(value) ? "".concat(key, "=").concat(value.join(',')) : "".concat(key, "=").concat(value);
                }).join('&');
                return sendRequest({
                    method: 'post',
                    url: "/businesses/".concat(businessId, "/active-services-report").concat(queryParams !== '' ? "?".concat(queryParams) : ''),
                    urlForSentry: '/businesses/:businessId/active-services-report',
                    apiHost: APIHost.Scout,
                    data: {
                        services: services.length > 0 ? services : undefined,
                        customers: customers.length > 0 ? customers : undefined,
                        agreements: agreements.length > 0 ? agreements : undefined,
                        assignees: assignees.length > 0 ? assignees : undefined
                    }
                }).then(mapResponseBody).catch(()=>[]);
            },
            async remainingCreditReportServices () {
                return sendRequest({
                    method: 'get',
                    url: "/remaining-credit-report/services",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async remainingCreditReportCustomers () {
                return sendRequest({
                    method: 'get',
                    url: "/remaining-credit-report/customers",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async remainingCreditReportAgreements () {
                return sendRequest({
                    method: 'get',
                    url: "/remaining-credit-report/agreements",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async remainingCreditReport (param) {
                let { services, customers, agreements, sort } = param;
                return sendRequest({
                    method: 'post',
                    url: "/remaining-credit-report",
                    apiHost: APIHost.Scout,
                    data: {
                        services: services.length > 0 ? services : undefined,
                        customers: customers.length > 0 ? customers : undefined,
                        agreements: agreements.length > 0 ? customers : undefined,
                        sort
                    }
                }).then(mapResponseBody).then((entries)=>entries.map(RemainingCreditReportEntryModel.fromDTO)).catch(()=>[]);
            },
            async getFutureInvoicesReport (params) {
                const serializeArray = (array)=>array && array.length > 0 ? array.join(',') : undefined;
                return sendRequest({
                    method: 'get',
                    url: '/future-invoices',
                    urlForSentry: '/future-invoices',
                    apiHost: APIHost.Scout,
                    params: {
                        companyName: serializeArray(params.companies),
                        agreementName: serializeArray(params.agreements),
                        requiresApproval: serializeArray(params.requiresApproval),
                        issueDateFrom: params.issueDateFrom,
                        issueDateTo: params.issueDateTo,
                        dueDateFrom: params.dueDateFrom,
                        dueDateTo: params.dueDateTo,
                        sortField: params.sortField,
                        sortDirection: params.sortDirection
                    }
                }).then((res)=>res.data.map(FutureInvoiceModel.fromDTO));
            },
            async getFutureInvoicesByCustomer (customerId) {
                return sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: "/future-invoices/customer/".concat(customerId),
                    urlForSentry: '/future-invoices/customer/:customerId'
                }).then((res)=>res.data.map(FutureInvoiceModel.fromDTO));
            },
            async getFutureInvoicesCompanyNames () {
                return sendRequest({
                    method: 'get',
                    url: '/future-invoices/company-names',
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            },
            async getFutureInvoicesAgreementNames () {
                return sendRequest({
                    method: 'get',
                    url: '/future-invoices/agreement-names',
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            }
        },
        karbon: {
            async setAPIKey (apiKey) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/karbon',
                    urlForSentry: '/integrations/karbon',
                    data: {
                        type: 'apiKey',
                        apiKey: {
                            key: apiKey
                        }
                    }
                });
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/karbon/disconnect',
                    urlForSentry: '/integrations/karbon/disconnect'
                });
            },
            async getItems () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/karbon/items',
                    urlForSentry: '/integrations/karbon/items'
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.items : [];
            },
            async linkServiceTemplate (id, integratedIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/karbon/items/link',
                    urlForSentry: '/integrations/karbon/items/link',
                    data: {
                        id,
                        integratedIds
                    }
                });
            },
            async getRoles (serviceTemplateId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/karbon/".concat(serviceTemplateId, "/roles"),
                    urlForSentry: "/integrations/karbon/serviceTemplateId/roles"
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.roles || [] : [];
            },
            async getUsers () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/karbon/users',
                    urlForSentry: '/integrations/karbon/users'
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.users || [] : [];
            },
            async updateKarbonIntegration (defaultAssigneeId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/karbon/update",
                    urlForSentry: '/integrations/karbon/update',
                    data: {
                        source: 'karbon',
                        karbon: {
                            defaultAssigneeId
                        }
                    }
                });
            },
            async getIntegration () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/karbon",
                    urlForSentry: '/integrations/karbon'
                }).then(mapResponseBody);
            }
        },
        clientHub: {
            async setAPIKey (apiKey) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/client-hub',
                    urlForSentry: '/integrations/client-hub',
                    data: {
                        type: 'apiKey',
                        apiKey: {
                            key: apiKey
                        }
                    }
                });
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/client-hub/disconnect',
                    urlForSentry: '/integrations/client-hub/disconnect'
                });
            },
            async getItems () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/client-hub/items',
                    urlForSentry: '/integrations/client-hub/items'
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.items : [];
            },
            async linkServiceTemplate (id, integratedIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/client-hub/items/link',
                    urlForSentry: '/integrations/client-hub/items/link',
                    data: {
                        id,
                        integratedIds
                    }
                });
            },
            async getRoles (serviceTemplateId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/client-hub/".concat(serviceTemplateId, "/roles"),
                    urlForSentry: "/integrations/client-hub/serviceTemplateId/roles"
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.roles || [] : [];
            },
            async getUsers () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/client-hub/users',
                    urlForSentry: '/integrations/client-hub/users'
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.users || [] : [];
            }
        },
        keeper: {
            async connect (param) {
                let { clientId, clientSecret } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/keeper',
                    data: {
                        type: 'accessToken',
                        accessToken: {
                            clientId,
                            clientSecret
                        }
                    },
                    disableSentry: (err)=>err.hasServerError(ServerError.InvalidKeeperCredentials)
                });
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/keeper/disconnect'
                });
            },
            async getItems () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/keeper/items'
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.items || [] : [];
            },
            async linkServiceTemplate (id, integratedIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/keeper/items/link',
                    data: {
                        id,
                        integratedIds
                    }
                });
            }
        },
        xero: {
            async connect (param) {
                let { redirectUrl } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/accounting/xero/connect',
                    data: {
                        redirectUrl
                    }
                }).then(mapResponseBody);
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/accounting/xero/disconnect'
                }).then(mapResponseBody);
            }
        },
        slack: {
            async sendCsNotification (text) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/slack/cs-notification',
                    data: {
                        text
                    },
                    disableSentry: ()=>true
                });
            },
            async sendPayoutValidation (text) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/slack/payout-validation',
                    data: {
                        text
                    },
                    disableSentry: ()=>true
                });
            }
        },
        agreementImport: {
            async extract (data) {
                const response = await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agreement-import/v2/extract',
                    data,
                    disableSentry: (err)=>err.hasStatus([
                            422
                        ])
                }).then(mapResponseBody).catch((err)=>{
                    var _err_response;
                    const body = (_err_response = err.response) === null || _err_response === void 0 ? void 0 : _err_response.data;
                    if ((body === null || body === void 0 ? void 0 : body.reason) != null) {
                        return {
                            reason: body.reason,
                            conversationId: body.conversationId
                        };
                    }
                    throw err;
                });
                if ('reason' in response) {
                    return {
                        success: false,
                        reason: response.reason,
                        conversationId: response.conversationId
                    };
                }
                const { conversationId } = response, extractResult = _object_without_properties(response, [
                    "conversationId"
                ]);
                return {
                    success: true,
                    conversationId,
                    extraction: AgreementImportExtractionModel.fromDTO(extractResult)
                };
            },
            async extractOld (data) {
                const response = await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agreement-import/extract',
                    urlForSentry: '/api/agreement-import/extract',
                    data,
                    disableSentry: (err)=>err.hasStatus([
                            422
                        ])
                }).then(mapResponseBody).catch((err)=>{
                    var _err_response;
                    const body = (_err_response = err.response) === null || _err_response === void 0 ? void 0 : _err_response.data;
                    if ((body === null || body === void 0 ? void 0 : body.reason) != null) {
                        return {
                            reason: body.reason,
                            conversationId: body.conversationId
                        };
                    }
                    throw err;
                });
                if ('reason' in response) {
                    return {
                        success: false,
                        reason: response.reason,
                        conversationId: response.conversationId
                    };
                }
                const { conversationId } = response, extractResult = _object_without_properties(response, [
                    "conversationId"
                ]);
                return {
                    success: true,
                    conversationId,
                    extraction: AgreementImportExtractionModel.fromDTO(extractResult)
                };
            },
            async feedback (data) {
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agreement-import/".concat(data.conversationId, "/feedback"),
                    urlForSentry: '/api/agreement-import/:conversationId/feedback',
                    data: {
                        rating: data.rating
                    }
                });
            },
            async create (data) {
                const dto = data.extraction.toDTO();
                const body = {
                    agreementName: dto.agreementName,
                    effectiveDate: dto.effectiveDate,
                    netTerms: dto.netTerms,
                    hasClientSignature: dto.hasClientSignature,
                    client: dto.client,
                    services: dto.services,
                    legalTerms: dto.legalTerms
                };
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agreement-import/".concat(data.conversationId, "/create"),
                    urlForSentry: '/api/agreement-import/:conversationId/create',
                    data: body
                }).then(mapResponseBody);
            }
        },
        agenticDraft: {
            create: async ()=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agent/draft',
                    data: {}
                }).then(mapResponseBody);
            },
            createForDraft: async (draftId)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/draft/".concat(draftId),
                    urlForSentry: '/api/agent/draft/:draftId',
                    data: {}
                }).then(mapResponseBody);
            },
            findByDraft: async (draftId)=>{
                try {
                    const body = await sendRequest({
                        apiHost: APIHost.AI,
                        method: 'get',
                        url: "/api/agent/draft/by-draft/".concat(draftId),
                        urlForSentry: '/api/agent/draft/by-draft/:draftId',
                        disableSentry: (e)=>e.status === 404
                    }).then(mapResponseBody);
                    return ConversationModel.fromDTO(body);
                } catch (err) {
                    const wrapped = new ErrorResponse(err);
                    if (wrapped.status === 404) return null;
                    throw err;
                }
            },
            attachDraft: async (conversationId, draftId)=>{
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/draft/".concat(conversationId, "/attach-draft"),
                    urlForSentry: '/api/agent/draft/:conversationId/attach-draft',
                    data: {
                        draftId
                    }
                });
            },
            get: async (conversationId)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: "/api/agent/draft/".concat(conversationId),
                    urlForSentry: '/api/agent/draft/:conversationId'
                }).then(mapResponseBody).then(ConversationModel.fromDTO);
            },
            feedback: async (param)=>{
                let { conversationId, messageId, rating, comment } = param;
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/draft/".concat(conversationId, "/feedback"),
                    urlForSentry: '/api/agent/draft/:conversationId/feedback',
                    data: {
                        messageId,
                        rating,
                        comment
                    }
                });
            },
            dismissFeedback: async (param)=>{
                let { conversationId, messageId } = param;
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/draft/".concat(conversationId, "/feedback/dismiss"),
                    urlForSentry: '/api/agent/draft/:conversationId/feedback/dismiss',
                    data: {
                        messageId
                    }
                });
            },
            sendMessage: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/draft/".concat(args.conversationId, "/messages"),
                    urlForSentry: '/api/agent/draft/:conversationId/messages',
                    data: args.content,
                    onEvent: args.onEvent,
                    parseEvent: createAgentStreamParser(),
                    signal: args.signal
                });
            },
            undo: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/draft/".concat(args.conversationId, "/undo"),
                    urlForSentry: '/api/agent/draft/:conversationId/undo',
                    data: {
                        messageId: args.messageId
                    },
                    onEvent: args.onEvent,
                    parseEvent: createAgentStreamParser(),
                    signal: args.signal
                });
            }
        },
        analyticAgent: {
            create: async ()=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agent/analyst',
                    data: {}
                }).then(mapResponseBody);
            },
            get: async (id)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: "/api/agent/analyst/".concat(id),
                    urlForSentry: '/api/agent/analyst/:id'
                }).then(mapResponseBody);
            },
            feedback: async (args)=>{
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/analyst/".concat(args.conversationId, "/feedback"),
                    urlForSentry: '/api/agent/analyst/:conversationId/feedback',
                    data: {
                        messageId: args.messageId,
                        rating: args.rating,
                        comment: args.comment
                    }
                });
            },
            sendMessage: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/analyst/".concat(args.conversationId, "/messages"),
                    urlForSentry: '/api/agent/analyst/:conversationId/messages',
                    data: {
                        text: args.content
                    },
                    onEvent: args.onEvent,
                    parseEvent: createAnalystStreamParser(),
                    signal: args.signal
                });
            }
        },
        customPageTemplates: {
            async getAll (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/custom-page-template"),
                    urlForSentry: '/business/:businessId/custom-page-template'
                }).then(mapResponseBody);
            },
            async getById (businessId, templateId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/custom-page-template/").concat(templateId),
                    urlForSentry: '/business/:businessId/custom-page-template/:templateId'
                }).then(mapResponseBody);
            },
            async create (businessId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page-template/create"),
                    urlForSentry: '/business/:businessId/custom-page-template/create',
                    data,
                    disableSentry: (err)=>err.hasServerError(ServerError.CustomPageTemplateDuplicateName)
                }).then(mapResponseBody);
            },
            async update (businessId, templateId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page-template/").concat(templateId, "/update"),
                    urlForSentry: '/business/:businessId/custom-page-template/:templateId/update',
                    data,
                    disableSentry: (err)=>err.hasServerError(ServerError.CustomPageTemplateDuplicateName)
                });
            },
            async duplicate (businessId, templateId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page-template/").concat(templateId, "/duplicate"),
                    urlForSentry: '/business/:businessId/custom-page-template/:templateId/duplicate'
                }).then(mapResponseBody);
            },
            async delete (businessId, templateId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page-template/").concat(templateId, "/delete"),
                    urlForSentry: '/business/:businessId/custom-page-template/:templateId/delete'
                });
            }
        },
        customPage: {
            async createText (businessId, sourceId, content) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page/text"),
                    urlForSentry: '/business/:businessId/custom-page/text',
                    data: {
                        sourceId,
                        content
                    }
                }).then(mapResponseBody);
            },
            async updateText (businessId, textId, sourceId, content) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page/text/").concat(textId, "/update"),
                    urlForSentry: '/business/:businessId/custom-page/text/:textId/update',
                    data: {
                        sourceId,
                        content
                    }
                }).then(mapResponseBody);
            },
            async getText (sourceId, textId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/texts/".concat(sourceId, "/").concat(textId),
                    urlForSentry: '/texts/:sourceId/:id'
                }).then(mapResponseBody);
            },
            async getDownloadUrl (businessId, pdfName) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/custom-page/").concat(encodeURIComponent(pdfName), "/download"),
                    urlForSentry: '/business/:businessId/custom-page/:pdfName/download'
                }).then(mapResponseBody);
            }
        },
        branding: {
            extractFromWebsite (websiteUrl) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: "/api/branding/extract?url=".concat(encodeURIComponent(websiteUrl)),
                    urlForSentry: '/api/branding/extract'
                }).then(mapResponseBody);
            }
        },
        oauth: {
            issueCode (payload) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/internal/oauth/issue-code',
                    data: payload
                }).then(mapResponseBody);
            }
        },
        pdf: {
            generateRelationshipPdf (relationshipId, lang) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/generate-relationship-pdf/".concat(relationshipId),
                    urlForSentry: '/generate-relationship-pdf/:relationshipId',
                    params: {
                        lang
                    },
                    data: {}
                }).then(mapResponseBody);
            }
        },
        jobs: {
            getJobs () {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'get',
                    url: '/jobs'
                }).then(mapResponseBody);
            },
            cancelJob (jobId) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'delete',
                    url: "/jobs/".concat(jobId),
                    urlForSentry: '/jobs/:jobId'
                }).then(mapResponseBody);
            },
            markJobsSeen (entries) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: '/jobs',
                    data: entries
                }).then(mapResponseBody);
            }
        }
    };
}
